// src/client-repository.ts
import {
  newId
} from "@grimoire/shared";
import {
  ContentType,
  ContentOrigin,
  PublicationState,
  PrivacyState,
  PersonalState,
  ReadingPriority,
  ProgressMethod,
  ProgressConfidence,
  AccessPathKind,
  AccessCapability,
  SourceHealth,
  toWorkSummary,
  validateWorkInvariant,
  validateMemoryInvariant,
  createWorkRelation,
  validateWorkRelationInvariant,
  ArchiveReason,
  createArchiveRecord,
  calculateTitleSimilarity,
  calculateUrlSimilarity
} from "@grimoire/domain";
var STORAGE_KEY = "cv:local_vault:v1";
var ARCHIVE_STORAGE_KEY = "cv:archive_records:v1";
var RELATIONS_STORAGE_KEY = "cv:work_relations:v1";
var DEFAULT_USER_ACCOUNT = {
  id: "archivist",
  username: "Archivist",
  displayName: "Archivist",
  role: "Archivist"
};
var AVAILABLE_USER_PROFILES = [];
var DEFAULT_WORK_COVERS = {};
function createInitialArchiveSeed() {
  return { works: [], records: [] };
}
var ClientRepository = class {
  items = /* @__PURE__ */ new Map();
  archiveRecords = /* @__PURE__ */ new Map();
  workRelations = /* @__PURE__ */ new Map();
  persistStorage;
  constructor(options) {
    this.persistStorage = !options?.empty;
    if (!options?.empty) {
      this.loadFromStorage();
    }
  }
  syncListeners = /* @__PURE__ */ new Set();
  storageListenerAttached = false;
  subscribeToStorage(listener) {
    this.syncListeners.add(listener);
    this.ensureStorageListener();
    return () => this.syncListeners.delete(listener);
  }
  notifyStorageChangeForTesting() {
    this.loadFromStorage();
    for (const cb of this.syncListeners) {
      try {
        cb();
      } catch {
      }
    }
  }
  ensureStorageListener() {
    if (this.storageListenerAttached) return;
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      this.storageListenerAttached = true;
      window.addEventListener("storage", (event) => {
        if (event.key === STORAGE_KEY || event.key === ARCHIVE_STORAGE_KEY || event.key === RELATIONS_STORAGE_KEY) {
          this.loadFromStorage();
          for (const cb of this.syncListeners) {
            try {
              cb();
            } catch {
            }
          }
        }
      });
    }
  }
  loadFromStorage() {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            this.items.clear();
            for (const item of parsed) {
              this.items.set(item.work.id, item);
            }
          }
        }
        const rawArchive = window.localStorage.getItem(ARCHIVE_STORAGE_KEY);
        if (rawArchive) {
          const parsedArchive = JSON.parse(rawArchive);
          if (Array.isArray(parsedArchive)) {
            for (const rec of parsedArchive) {
              this.archiveRecords.set(rec.id, rec);
            }
          }
        }
        const rawRelations = window.localStorage.getItem(RELATIONS_STORAGE_KEY);
        if (rawRelations) {
          const parsedRelations = JSON.parse(rawRelations);
          if (Array.isArray(parsedRelations)) {
            for (const rel of parsedRelations) {
              this.workRelations.set(rel.id, rel);
            }
          }
        }
      } catch (err) {
        console.warn("Failed to load Grimoire cache from localStorage:", err);
      }
    }
  }
  saveToStorage() {
    if (this.persistStorage && typeof window !== "undefined" && window.localStorage) {
      try {
        const arr = Array.from(this.items.values());
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
        const archiveArr = Array.from(this.archiveRecords.values());
        window.localStorage.setItem(ARCHIVE_STORAGE_KEY, JSON.stringify(archiveArr));
        const relationsArr = Array.from(this.workRelations.values());
        window.localStorage.setItem(RELATIONS_STORAGE_KEY, JSON.stringify(relationsArr));
      } catch (err) {
        console.warn("Failed to save Grimoire state to localStorage:", err);
      }
    }
  }
  // Work queries
  getItems() {
    return Array.from(this.items.values());
  }
  getItem(id) {
    return this.items.get(id);
  }
  getSummaries() {
    return Array.from(this.items.values()).filter((i) => !i.work.deletedAt).map((i) => toWorkSummary(i.work));
  }
  // Work mutations
  createWork(workParams, initialState = PersonalState.Reading, initialAnchor, initialPath) {
    validateWorkInvariant(workParams);
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const id = newId();
    const work = {
      ...workParams,
      id,
      createdAt: now,
      updatedAt: now,
      deletedAt: null
    };
    const pathId = newId();
    const accessPaths = initialPath ? [
      {
        id: pathId,
        workId: id,
        kind: AccessPathKind.Web,
        provider: initialPath.provider,
        label: initialPath.label,
        url: initialPath.url ?? "",
        capabilities: [AccessCapability.Read, AccessCapability.Updates],
        health: SourceHealth.Healthy,
        healthState: SourceHealth.Healthy,
        lastCheckedAt: now,
        lastOpenedAt: now,
        isPreferred: true,
        provenance: { source: "work_creation" },
        createdAt: now,
        updatedAt: now
      }
    ] : [];
    const relId = newId();
    const relationship = {
      id: relId,
      workId: id,
      personalState: initialState,
      priority: ReadingPriority.Normal,
      startedAt: initialState === PersonalState.Reading ? now : null,
      lastActiveAt: now,
      completedAt: initialState === PersonalState.Completed ? now : null,
      stoppedAt: null,
      stoppedReason: null,
      stoppedNote: null,
      currentAnchor: initialAnchor ?? { type: "chapter", chapter: 1, title: "Chapter 1" },
      preferredAccessPathId: accessPaths[0]?.id ?? null,
      createdAt: now,
      updatedAt: now
    };
    const item = {
      work,
      relationship,
      accessPaths,
      memories: [],
      updates: []
    };
    this.items.set(id, item);
    this.saveToStorage();
    return item;
  }
  updateWork(id, patch) {
    if (patch.privacyState === PrivacyState.Vault) {
      throw new Error("Vault works must be encrypted in the private vault repository.");
    }
    const item = this.items.get(id);
    if (!item) throw new Error(`Work with ID ${id} not found`);
    item.work = {
      ...item.work,
      ...patch,
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
    validateWorkInvariant(item.work);
    this.saveToStorage();
    return item.work;
  }
  deleteWork(id) {
    const item = this.items.get(id);
    if (item) {
      item.work = {
        ...item.work,
        deletedAt: (/* @__PURE__ */ new Date()).toISOString()
      };
      this.saveToStorage();
    }
  }
  purgeWork(id) {
    const deleted = this.items.delete(id);
    if (deleted) {
      for (const [relId, rel] of Array.from(this.workRelations.entries())) {
        if (rel.sourceWorkId === id || rel.targetWorkId === id) {
          this.workRelations.delete(relId);
        }
      }
      this.saveToStorage();
    }
    return deleted;
  }
  purgeAllDeletedWorks() {
    let count = 0;
    for (const [id, item] of Array.from(this.items.entries())) {
      if (item.work.deletedAt) {
        this.items.delete(id);
        for (const [relId, rel] of Array.from(this.workRelations.entries())) {
          if (rel.sourceWorkId === id || rel.targetWorkId === id) {
            this.workRelations.delete(relId);
          }
        }
        count++;
      }
    }
    if (count > 0) {
      this.saveToStorage();
    }
    return count;
  }
  resetToDefaultSeed() {
    this.items.clear();
    this.workRelations.clear();
    this.archiveRecords.clear();
    this.saveToStorage();
  }
  clear() {
    this.resetToDefaultSeed();
  }
  importData(rawInput) {
    let payload = rawInput;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch {
        throw new Error("Invalid JSON file content");
      }
    }
    if (!payload || typeof payload !== "object") {
      throw new Error("Import archive is empty or invalid");
    }
    let detectedUser = void 0;
    if (payload.account?.username) detectedUser = String(payload.account.username);
    else if (payload.user?.username) detectedUser = String(payload.user.username);
    else if (typeof payload.account === "string") detectedUser = payload.account;
    else if (typeof payload.username === "string") detectedUser = payload.username;
    else if (payload.profile?.username) detectedUser = String(payload.profile.username);
    let worksCount = 0;
    let memoriesCount = 0;
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (item && item.work && item.work.id) {
          this.items.set(item.work.id, item);
          worksCount++;
          if (Array.isArray(item.memories)) {
            memoriesCount += item.memories.length;
          }
        }
      }
    } else {
      const worksList = Array.isArray(payload.works) ? payload.works : Array.isArray(payload.items) ? payload.items : [];
      for (const w of worksList) {
        if (!w) continue;
        if (w.work && w.work.id) {
          this.items.set(w.work.id, w);
          worksCount++;
          if (Array.isArray(w.memories)) {
            memoriesCount += w.memories.length;
          }
        } else if (w.id && w.title) {
          const workId = w.id;
          const workObj = {
            id: workId,
            title: String(w.title),
            alternativeTitles: Array.isArray(w.alternativeTitles) ? w.alternativeTitles : [],
            aliases: Array.isArray(w.aliases) ? w.aliases : [],
            description: w.description ?? null,
            contentType: w.contentType || ContentType.WebNovel,
            origin: w.origin || ContentOrigin.Unknown,
            publicationState: w.publicationState || PublicationState.Unknown,
            privacyState: w.privacyState || PrivacyState.Public,
            coverRef: w.coverRef ?? null,
            genres: Array.isArray(w.genres) ? w.genres : [],
            tags: Array.isArray(w.tags) ? w.tags : [],
            externalIds: Array.isArray(w.externalIds) ? w.externalIds : [],
            createdAt: w.createdAt ?? (/* @__PURE__ */ new Date()).toISOString(),
            updatedAt: w.updatedAt ?? (/* @__PURE__ */ new Date()).toISOString(),
            deletedAt: w.deletedAt ? String(w.deletedAt) : null
          };
          const paths = Array.isArray(w.accessPaths) ? w.accessPaths.map((p) => ({
            id: p.id ?? newId(),
            workId,
            kind: p.kind ?? AccessPathKind.Web,
            label: p.label ?? "Source",
            url: p.url ?? "",
            provider: p.provider ?? "import",
            isPreferred: Boolean(p.isPreferred),
            health: p.health ?? SourceHealth.Unknown,
            capabilities: p.capabilities ?? [AccessCapability.Read],
            provenance: p.provenance ?? null,
            lastOpenedAt: p.lastOpenedAt ?? null
          })) : [];
          this.items.set(workId, {
            work: workObj,
            relationship: w.relationship,
            accessPaths: paths,
            latestProgress: w.latestProgress,
            memories: Array.isArray(w.memories) ? w.memories : [],
            updates: Array.isArray(w.updates) ? w.updates : []
          });
          worksCount++;
        }
      }
      if (Array.isArray(payload.memories)) {
        for (const m of payload.memories) {
          if (m && m.workId && this.items.has(m.workId)) {
            const target = this.items.get(m.workId);
            if (!target.memories.some((existing) => existing.id === m.id)) {
              const memoryRecord = {
                id: m.id || newId(),
                workId: m.workId,
                type: m.type || "note",
                title: m.title || "",
                body: m.body || "",
                privacyState: m.privacyState === "vault" ? "vault" : m.privacyState === "private" ? "private" : "public",
                spoilerLevel: m.spoilerLevel || "spoiler_free",
                showInGlobalGallery: m.showInGlobalGallery ?? true,
                tags: Array.isArray(m.tags) ? m.tags : [],
                assets: Array.isArray(m.assets) ? m.assets : m.mediaUrl ? [{ id: `asset-${m.id}`, fileId: m.mediaUrl, mimeType: m.mediaMimeType || "image/png" }] : [],
                accessPathId: m.accessPathId ?? null,
                progressAnchor: m.progressAnchor ?? (m.chapterContext ? { type: "chapter", chapter: 1, title: m.chapterContext } : null),
                createdAt: m.createdAt ?? (/* @__PURE__ */ new Date()).toISOString(),
                updatedAt: m.updatedAt ?? (/* @__PURE__ */ new Date()).toISOString(),
                deletedAt: null
              };
              target.memories.push(memoryRecord);
              memoriesCount++;
            }
          }
        }
      }
      if (Array.isArray(payload.relations)) {
        for (const r of payload.relations) {
          if (r && r.id) {
            this.workRelations.set(r.id, r);
          }
        }
      }
      if (Array.isArray(payload.archiveRecords)) {
        for (const ar of payload.archiveRecords) {
          if (ar && ar.id) {
            this.archiveRecords.set(ar.id, ar);
          }
        }
      }
      if (Array.isArray(payload.updates)) {
        for (const upd of payload.updates) {
          if (upd && upd.workId && this.items.has(upd.workId)) {
            const target = this.items.get(upd.workId);
            if (!target.updates.some((existing) => existing.id === upd.id)) {
              target.updates.push(upd);
            }
          }
        }
      }
      if (Array.isArray(payload.categories) && payload.categories.length > 0 && typeof window !== "undefined") {
        try {
          const rawStored = window.localStorage.getItem("cv:custom_categories:v1");
          const existing = rawStored ? JSON.parse(rawStored) : [];
          const existingIds = new Set(existing.map((c) => c.id));
          const toAdd = payload.categories.filter((c) => c && c.id && !existingIds.has(c.id));
          if (toAdd.length > 0) {
            window.localStorage.setItem("cv:custom_categories:v1", JSON.stringify([...existing, ...toAdd]));
          }
        } catch {
        }
      }
      if (Array.isArray(payload.credentials) && payload.credentials.length > 0 && typeof window !== "undefined") {
        try {
          const rawCreds = window.localStorage.getItem("grimoire:vault:credentials");
          const existing = rawCreds ? JSON.parse(rawCreds) : [];
          const existingIds = new Set(existing.map((c) => c.id));
          const toAdd = payload.credentials.filter((c) => c && c.id && !existingIds.has(c.id));
          if (toAdd.length > 0) {
            window.localStorage.setItem("grimoire:vault:credentials", JSON.stringify([...existing, ...toAdd]));
          }
        } catch {
        }
      }
    }
    this.saveToStorage();
    return {
      worksCount,
      memoriesCount,
      user: detectedUser
    };
  }
  // Reading relationship operations
  updatePersonalState(workId, state, stoppedReason, stoppedNote) {
    const item = this.items.get(workId);
    if (!item) throw new Error(`Work with ID ${workId} not found`);
    const now = (/* @__PURE__ */ new Date()).toISOString();
    if (!item.relationship) {
      item.relationship = {
        id: newId(),
        workId,
        personalState: state,
        priority: ReadingPriority.Normal,
        startedAt: state === PersonalState.Reading ? now : null,
        lastActiveAt: now,
        completedAt: state === PersonalState.Completed ? now : null,
        stoppedAt: state === PersonalState.Paused || state === PersonalState.Stalled ? now : null,
        stoppedReason: stoppedReason ?? null,
        stoppedNote: stoppedNote ?? null,
        currentAnchor: { type: "chapter", chapter: 1 },
        preferredAccessPathId: item.accessPaths[0]?.id ?? null,
        createdAt: now,
        updatedAt: now
      };
    } else {
      item.relationship = {
        ...item.relationship,
        personalState: state,
        lastActiveAt: now,
        completedAt: state === PersonalState.Completed ? now : item.relationship.completedAt,
        stoppedAt: state === PersonalState.Paused || state === PersonalState.Stalled ? now : item.relationship.stoppedAt,
        stoppedReason: stoppedReason !== void 0 ? stoppedReason : item.relationship.stoppedReason,
        stoppedNote: stoppedNote !== void 0 ? stoppedNote : item.relationship.stoppedNote,
        updatedAt: now
      };
    }
    this.saveToStorage();
    return item.relationship;
  }
  updateRelationship(workId, patch) {
    const item = this.items.get(workId);
    if (!item) throw new Error(`Work with ID ${workId} not found`);
    const now = (/* @__PURE__ */ new Date()).toISOString();
    if (!item.relationship) {
      item.relationship = {
        id: newId(),
        workId,
        personalState: PersonalState.Reading,
        priority: ReadingPriority.Normal,
        startedAt: now,
        lastActiveAt: now,
        completedAt: null,
        stoppedAt: null,
        stoppedReason: null,
        stoppedNote: null,
        currentAnchor: { type: "chapter", chapter: 1 },
        preferredAccessPathId: item.accessPaths[0]?.id ?? null,
        ...patch,
        createdAt: now,
        updatedAt: now
      };
    } else {
      item.relationship = {
        ...item.relationship,
        ...patch,
        updatedAt: now
      };
    }
    this.saveToStorage();
    return item.relationship;
  }
  recordProgress(workId, newAnchor, method = ProgressMethod.Manual) {
    const item = this.items.get(workId);
    if (!item) throw new Error(`Work with ID ${workId} not found`);
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const anchorBefore = item.relationship?.currentAnchor ?? null;
    const event = {
      id: newId(),
      workId,
      readingRelationshipId: item.relationship?.id,
      accessPathId: item.relationship?.preferredAccessPathId ?? void 0,
      method,
      confidence: ProgressConfidence.Confirmed,
      anchorBefore: anchorBefore ?? void 0,
      anchorAfter: newAnchor,
      createdAt: now
    };
    if (item.relationship) {
      item.relationship = {
        ...item.relationship,
        currentAnchor: newAnchor,
        lastActiveAt: now,
        updatedAt: now
      };
    }
    item.latestProgress = event;
    this.saveToStorage();
    return event;
  }
  // Access Path operations
  addAccessPath(workId, path) {
    const item = this.items.get(workId);
    if (!item) throw new Error(`Work with ID ${workId} not found`);
    const newPath = {
      ...path,
      id: newId(),
      workId
    };
    if (newPath.isPreferred) {
      item.accessPaths = item.accessPaths.map((p) => ({
        ...p,
        isPreferred: false
      }));
      if (item.relationship) {
        item.relationship = {
          ...item.relationship,
          preferredAccessPathId: newPath.id,
          updatedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
      }
    }
    item.accessPaths = [...item.accessPaths, newPath];
    this.saveToStorage();
    return newPath;
  }
  setPreferredAccessPath(workId, pathId) {
    const item = this.items.get(workId);
    if (!item) return;
    item.accessPaths = item.accessPaths.map((p) => ({
      ...p,
      isPreferred: p.id === pathId
    }));
    if (item.relationship) {
      item.relationship = {
        ...item.relationship,
        preferredAccessPathId: pathId,
        updatedAt: (/* @__PURE__ */ new Date()).toISOString()
      };
    }
    this.saveToStorage();
  }
  deleteAccessPath(workId, pathId) {
    const item = this.items.get(workId);
    if (!item) return;
    item.accessPaths = item.accessPaths.filter((p) => p.id !== pathId);
    if (item.relationship?.preferredAccessPathId === pathId) {
      const fallback = item.accessPaths.find((p) => p.isPreferred) ?? item.accessPaths[0] ?? null;
      item.relationship = {
        ...item.relationship,
        preferredAccessPathId: fallback?.id ?? null,
        updatedAt: (/* @__PURE__ */ new Date()).toISOString()
      };
    }
    this.saveToStorage();
  }
  updateAccessPathHealth(workId, pathId, monitorState) {
    const item = this.items.get(workId);
    if (!item) return null;
    const pathIndex = item.accessPaths.findIndex((p) => p.id === pathId);
    if (pathIndex === -1) return null;
    const currentPath = item.accessPaths[pathIndex];
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const updatedPath = {
      ...currentPath,
      health: monitorState.health,
      healthState: monitorState.health,
      lastCheckedAt: monitorState.lastCheckedAt ?? now,
      healthMonitorState: monitorState,
      updatedAt: now
    };
    item.accessPaths[pathIndex] = updatedPath;
    this.saveToStorage();
    return updatedPath;
  }
  // Memory operations
  listMemories() {
    return Array.from(this.items.values()).filter((item) => !item.work.deletedAt).flatMap(
      (item) => item.memories.filter((memory) => !memory.deletedAt).map((memory) => ({ memory, workTitle: item.work.title }))
    ).sort((a, b) => b.memory.createdAt.localeCompare(a.memory.createdAt));
  }
  addMemory(workId, memory) {
    if (memory.privacyState === PrivacyState.Vault) {
      throw new Error("Vault memories must be encrypted in the private vault repository.");
    }
    const item = this.items.get(workId);
    if (!item) throw new Error(`Work with ID ${workId} not found`);
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const id = newId();
    const newMem = {
      ...memory,
      id,
      workId,
      accessPathId: memory.accessPathId ?? null,
      progressAnchor: memory.progressAnchor ?? null,
      assets: (memory.assets ?? []).map((asset) => ({ ...asset, memoryId: id })),
      tags: memory.tags ?? [],
      showInGlobalGallery: memory.showInGlobalGallery ?? true,
      createdAt: now,
      updatedAt: now
    };
    validateMemoryInvariant(newMem);
    if (newMem.accessPathId && !item.accessPaths.some((path) => path.id === newMem.accessPathId)) {
      throw new Error("Memory access path must belong to its Work.");
    }
    item.memories = [...item.memories, newMem];
    this.saveToStorage();
    return newMem;
  }
  updateMemory(memoryId, patch) {
    if (patch.privacyState === PrivacyState.Vault) {
      throw new Error("Vault memories must be encrypted in the private vault repository.");
    }
    for (const item of this.items.values()) {
      const index = item.memories.findIndex((memory) => memory.id === memoryId);
      if (index < 0) continue;
      const existing = item.memories[index];
      if (!existing) break;
      const updated = { ...existing, ...patch, updatedAt: (/* @__PURE__ */ new Date()).toISOString() };
      validateMemoryInvariant(updated);
      item.memories[index] = updated;
      this.saveToStorage();
      return updated;
    }
    throw new Error(`Memory with ID ${memoryId} not found.`);
  }
  deleteMemory(memoryId) {
    return this.softDeleteMemory(memoryId);
  }
  softDeleteMemory(memoryId) {
    for (const item of this.items.values()) {
      const index = item.memories.findIndex((memory) => memory.id === memoryId);
      if (index < 0) continue;
      const mem = item.memories[index];
      if (!mem) break;
      item.memories[index] = {
        ...mem,
        deletedAt: (/* @__PURE__ */ new Date()).toISOString(),
        updatedAt: (/* @__PURE__ */ new Date()).toISOString()
      };
      this.saveToStorage();
      return true;
    }
    return false;
  }
  restoreMemory(memoryId) {
    for (const item of this.items.values()) {
      const index = item.memories.findIndex((memory) => memory.id === memoryId);
      if (index < 0) continue;
      const mem = item.memories[index];
      if (!mem) break;
      item.memories[index] = {
        ...mem,
        deletedAt: null,
        updatedAt: (/* @__PURE__ */ new Date()).toISOString()
      };
      this.saveToStorage();
      return true;
    }
    return false;
  }
  purgeMemory(memoryId) {
    for (const item of this.items.values()) {
      const index = item.memories.findIndex((memory) => memory.id === memoryId);
      if (index < 0) continue;
      item.memories = item.memories.filter((memory) => memory.id !== memoryId);
      this.saveToStorage();
      return true;
    }
    return false;
  }
  // ---------------------------------------------------------------------------
  // Archive Operations
  // ---------------------------------------------------------------------------
  archiveWork(workId, reason, whyStoppedNote) {
    const item = this.items.get(workId);
    if (!item) {
      throw new Error(`Work with ID ${workId} not found.`);
    }
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const record = createArchiveRecord({
      workId,
      reason,
      note: whyStoppedNote ?? null,
      previousState: item.relationship?.personalState ?? null,
      previousProgress: item.relationship?.currentAnchor ?? null,
      archivedAt: now
    });
    this.archiveRecords.set(record.id, record);
    item.work = {
      ...item.work,
      deletedAt: now,
      updatedAt: now
    };
    if (item.relationship) {
      item.relationship = {
        ...item.relationship,
        stoppedReason: reason,
        stoppedNote: whyStoppedNote ?? null,
        stoppedAt: now,
        updatedAt: now
      };
    }
    this.saveToStorage();
    return record;
  }
  restoreWork(workId) {
    const item = this.items.get(workId);
    if (!item) {
      throw new Error(`Work with ID ${workId} not found.`);
    }
    item.work = {
      ...item.work,
      deletedAt: null,
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
    this.saveToStorage();
    return item.work;
  }
  getArchiveRecords() {
    return Array.from(this.archiveRecords.values());
  }
  getArchivedWorks() {
    return Array.from(this.items.values()).filter((item) => Boolean(item.work.deletedAt)).map((item) => {
      const records = Array.from(this.archiveRecords.values()).filter((r) => r.workId === item.work.id).sort((a, b) => b.archivedAt.localeCompare(a.archivedAt));
      return {
        work: item.work,
        record: records[0],
        relationship: item.relationship
      };
    });
  }
  // ---------------------------------------------------------------------------
  // Trash Bin & Soft Delete Operations
  // ---------------------------------------------------------------------------
  getDeletedWorks() {
    return Array.from(this.items.values()).filter((item) => Boolean(item.work.deletedAt)).sort((a, b) => (b.work.deletedAt ?? "").localeCompare(a.work.deletedAt ?? ""));
  }
  getDeletedMemories() {
    const list = [];
    for (const item of this.items.values()) {
      for (const mem of item.memories) {
        if (mem.deletedAt) {
          list.push({
            memory: mem,
            workTitle: item.work.title,
            workId: item.work.id
          });
        }
      }
    }
    return list.sort((a, b) => (b.memory.deletedAt ?? "").localeCompare(a.memory.deletedAt ?? ""));
  }
  getTrashCount() {
    const deletedWorks = Array.from(this.items.values()).filter((item) => Boolean(item.work.deletedAt)).length;
    let deletedMemories = 0;
    for (const item of this.items.values()) {
      for (const mem of item.memories) {
        if (mem.deletedAt) deletedMemories++;
      }
    }
    return deletedWorks + deletedMemories;
  }
  purgeExpiredTrash(retentionDays = 30) {
    const cutoffMs = Date.now() - retentionDays * 24 * 60 * 60 * 1e3;
    let worksPurged = 0;
    let memoriesPurged = 0;
    for (const [id, item] of Array.from(this.items.entries())) {
      if (item.work.deletedAt && new Date(item.work.deletedAt).getTime() < cutoffMs) {
        this.items.delete(id);
        worksPurged++;
      } else {
        const initialCount = item.memories.length;
        item.memories = item.memories.filter((m) => {
          if (m.deletedAt && new Date(m.deletedAt).getTime() < cutoffMs) {
            return false;
          }
          return true;
        });
        memoriesPurged += initialCount - item.memories.length;
      }
    }
    if (worksPurged > 0 || memoriesPurged > 0) {
      this.saveToStorage();
    }
    return { worksPurged, memoriesPurged };
  }
  purgeAllTrash() {
    let worksPurged = 0;
    let memoriesPurged = 0;
    for (const [id, item] of Array.from(this.items.entries())) {
      if (item.work.deletedAt) {
        this.items.delete(id);
        worksPurged++;
      } else {
        const initialCount = item.memories.length;
        item.memories = item.memories.filter((m) => !m.deletedAt);
        memoriesPurged += initialCount - item.memories.length;
      }
    }
    if (worksPurged > 0 || memoriesPurged > 0) {
      this.saveToStorage();
    }
    return { worksPurged, memoriesPurged };
  }
  // ---------------------------------------------------------------------------
  // Work Relation Operations
  // ---------------------------------------------------------------------------
  getWorkRelations(workId) {
    return Array.from(this.workRelations.values()).filter(
      (rel) => rel.sourceWorkId === workId || rel.targetWorkId === workId
    );
  }
  getAllWorkRelations() {
    return Array.from(this.workRelations.values());
  }
  linkWorkRelation(sourceWorkId, targetWorkId, relationType, notes) {
    validateWorkRelationInvariant({ sourceWorkId, targetWorkId });
    if (!this.items.has(sourceWorkId)) {
      throw new Error(`Source work ${sourceWorkId} not found.`);
    }
    if (!this.items.has(targetWorkId)) {
      throw new Error(`Target work ${targetWorkId} not found.`);
    }
    const existing = Array.from(this.workRelations.values()).find(
      (r) => r.sourceWorkId === sourceWorkId && r.targetWorkId === targetWorkId && r.relationType === relationType
    );
    if (existing) {
      return existing;
    }
    const relation = createWorkRelation({
      sourceWorkId,
      targetWorkId,
      relationType,
      notes: notes ?? null
    });
    this.workRelations.set(relation.id, relation);
    this.saveToStorage();
    return relation;
  }
  removeWorkRelation(relationId) {
    this.workRelations.delete(relationId);
    this.saveToStorage();
  }
  // ---------------------------------------------------------------------------
  // Deduplication & Merge Operations
  // ---------------------------------------------------------------------------
  findDuplicates(workId) {
    const targetItem = this.items.get(workId);
    if (!targetItem) return [];
    const candidates = /* @__PURE__ */ new Map();
    const targetWork = targetItem.work;
    const targetTitle = targetWork.title.trim().toLowerCase();
    for (const item of this.items.values()) {
      if (item.work.id === workId || item.work.deletedAt) continue;
      const candidateWork = item.work;
      let bestConfidence = 0;
      const matchedFields = [];
      if (targetWork.externalIds.length > 0 && candidateWork.externalIds.length > 0) {
        for (const ext1 of targetWork.externalIds) {
          for (const ext2 of candidateWork.externalIds) {
            if (ext1.provider === ext2.provider && ext1.externalId === ext2.externalId) {
              bestConfidence = Math.max(bestConfidence, 0.99);
              matchedFields.push(`externalId:${ext1.provider}`);
            }
          }
        }
      }
      const candTitle = candidateWork.title.trim().toLowerCase();
      if (targetTitle === candTitle) {
        bestConfidence = Math.max(bestConfidence, 0.95);
        matchedFields.push("exact_title");
      } else {
        const sim = calculateTitleSimilarity(targetWork.title, candidateWork.title);
        if (sim >= 0.7) {
          bestConfidence = Math.max(bestConfidence, sim);
          matchedFields.push("title_similarity");
        }
      }
      const allTargetAliases = [...targetWork.alternativeTitles, ...targetWork.aliases].map(
        (a) => a.trim().toLowerCase()
      );
      const allCandAliases = [...candidateWork.alternativeTitles, ...candidateWork.aliases].map(
        (a) => a.trim().toLowerCase()
      );
      for (const tAlias of allTargetAliases) {
        if (!tAlias) continue;
        if (candTitle === tAlias || allCandAliases.includes(tAlias)) {
          bestConfidence = Math.max(bestConfidence, 0.88);
          matchedFields.push("alias_match");
          break;
        }
      }
      for (const cAlias of allCandAliases) {
        if (!cAlias) continue;
        if (targetTitle === cAlias) {
          bestConfidence = Math.max(bestConfidence, 0.88);
          matchedFields.push("alias_match");
          break;
        }
      }
      for (const p1 of targetItem.accessPaths) {
        const u1 = "url" in p1 ? p1.url : void 0;
        if (!u1) continue;
        for (const p2 of item.accessPaths) {
          const u2 = "url" in p2 ? p2.url : void 0;
          if (!u2) continue;
          const urlSim = calculateUrlSimilarity(u1, u2);
          if (urlSim >= 0.8) {
            bestConfidence = Math.max(bestConfidence, 0.9);
            matchedFields.push("access_url");
          }
        }
      }
      if (bestConfidence >= 0.4) {
        candidates.set(candidateWork.id, {
          work: toWorkSummary(candidateWork),
          confidence: Math.round(bestConfidence * 100) / 100,
          matchedFields: Array.from(new Set(matchedFields))
        });
      }
    }
    return Array.from(candidates.values()).sort((a, b) => b.confidence - a.confidence);
  }
  executeMerge(targetId, secondaryId, options) {
    if (targetId === secondaryId) {
      throw new Error("Cannot merge a work into itself.");
    }
    const targetItem = this.items.get(targetId);
    const secondaryItem = this.items.get(secondaryId);
    if (!targetItem || !secondaryItem) {
      throw new Error(`Target work (${targetId}) or secondary work (${secondaryId}) not found.`);
    }
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const existingUrls = new Set(
      targetItem.accessPaths.map(
        (p) => "url" in p ? p.url : "filePath" in p ? p.filePath : ""
      ).filter(Boolean)
    );
    const transferredPaths = [];
    for (const path of secondaryItem.accessPaths) {
      const urlOrPath = "url" in path ? path.url : "filePath" in path ? path.filePath : "";
      if (!urlOrPath || !existingUrls.has(urlOrPath)) {
        transferredPaths.push({
          ...path,
          workId: targetId,
          isPreferred: false,
          updatedAt: now
        });
        if (urlOrPath) existingUrls.add(urlOrPath);
      }
    }
    targetItem.accessPaths = [...targetItem.accessPaths, ...transferredPaths];
    const transferredMemories = secondaryItem.memories.map((m) => ({
      ...m,
      workId: targetId,
      updatedAt: now
    }));
    targetItem.memories = [...targetItem.memories, ...transferredMemories];
    for (const [relId, rel] of this.workRelations.entries()) {
      if (rel.sourceWorkId === secondaryId || rel.targetWorkId === secondaryId) {
        const newSource = rel.sourceWorkId === secondaryId ? targetId : rel.sourceWorkId;
        const newTarget = rel.targetWorkId === secondaryId ? targetId : rel.targetWorkId;
        if (newSource !== newTarget) {
          this.workRelations.set(relId, {
            ...rel,
            sourceWorkId: newSource,
            targetWorkId: newTarget
          });
        } else {
          this.workRelations.delete(relId);
        }
      }
    }
    const targetRel = targetItem.relationship;
    const secondaryRel = secondaryItem.relationship;
    let chosenAnchor = targetRel?.currentAnchor ?? null;
    const targetChapter = targetRel?.currentAnchor && "chapter" in targetRel.currentAnchor && typeof targetRel.currentAnchor.chapter === "number" ? targetRel.currentAnchor.chapter : 0;
    const secChapter = secondaryRel?.currentAnchor && "chapter" in secondaryRel.currentAnchor && typeof secondaryRel.currentAnchor.chapter === "number" ? secondaryRel.currentAnchor.chapter : 0;
    if (options?.customAnchor) {
      chosenAnchor = options.customAnchor;
    } else if (options?.progressChoice === "secondary" || options?.preferredProgressWorkId === secondaryId) {
      chosenAnchor = secondaryRel?.currentAnchor ?? chosenAnchor;
    } else if (options?.progressChoice === "highest") {
      if (secChapter > targetChapter && secondaryRel?.currentAnchor) {
        chosenAnchor = secondaryRel.currentAnchor;
      }
    }
    if (!targetItem.relationship && secondaryItem.relationship) {
      targetItem.relationship = {
        ...secondaryItem.relationship,
        id: newId(),
        workId: targetId,
        currentAnchor: chosenAnchor ?? secondaryItem.relationship.currentAnchor,
        updatedAt: now
      };
    } else if (targetItem.relationship) {
      targetItem.relationship = {
        ...targetItem.relationship,
        currentAnchor: chosenAnchor ?? targetItem.relationship.currentAnchor,
        lastActiveAt: now,
        updatedAt: now
      };
    }
    const combinedAlt = Array.from(
      /* @__PURE__ */ new Set([...targetItem.work.alternativeTitles, ...secondaryItem.work.alternativeTitles])
    );
    const combinedAliases = Array.from(
      /* @__PURE__ */ new Set([...targetItem.work.aliases, ...secondaryItem.work.aliases])
    );
    const combinedGenres = Array.from(
      /* @__PURE__ */ new Set([...targetItem.work.genres, ...secondaryItem.work.genres])
    );
    const combinedTags = Array.from(
      /* @__PURE__ */ new Set([...targetItem.work.tags, ...secondaryItem.work.tags])
    );
    const extIdMap = /* @__PURE__ */ new Map();
    for (const ext of targetItem.work.externalIds) {
      extIdMap.set(`${ext.provider}:${ext.externalId}`, ext);
    }
    for (const ext of secondaryItem.work.externalIds) {
      extIdMap.set(`${ext.provider}:${ext.externalId}`, ext);
    }
    let coverRef = targetItem.work.coverRef;
    if (options?.preferredCoverWorkId === secondaryId || !coverRef && secondaryItem.work.coverRef) {
      coverRef = secondaryItem.work.coverRef;
    }
    targetItem.work = {
      ...targetItem.work,
      alternativeTitles: combinedAlt,
      aliases: combinedAliases,
      genres: combinedGenres,
      tags: combinedTags,
      externalIds: Array.from(extIdMap.values()),
      coverRef,
      updatedAt: now
    };
    validateWorkInvariant(targetItem.work);
    const archiveRecord = createArchiveRecord({
      workId: secondaryId,
      reason: ArchiveReason.Duplicate,
      note: options?.whyMergedNote ?? `Merged into ${targetItem.work.title} (${targetId})`,
      previousState: secondaryItem.relationship?.personalState ?? null,
      previousProgress: secondaryItem.relationship?.currentAnchor ?? null,
      archivedAt: now
    });
    this.archiveRecords.set(archiveRecord.id, archiveRecord);
    secondaryItem.work = {
      ...secondaryItem.work,
      deletedAt: now,
      updatedAt: now
    };
    secondaryItem.accessPaths = [];
    secondaryItem.memories = [];
    this.saveToStorage();
    return targetItem.work;
  }
};
var clientRepository = new ClientRepository();

// src/repository-interface.ts
var BrowserRepositoryAdapter = ClientRepository;
var browserRepository = clientRepository;
var activeRepository = clientRepository;
function getActiveRepository() {
  return activeRepository;
}
function setActiveRepository(repo) {
  activeRepository = repo;
}
var getActiveClientRepository = getActiveRepository;
var setActiveClientRepository = setActiveRepository;

// src/stores/use-library-store.ts
import { create } from "zustand";
import {
  ProgressMethod as ProgressMethod2
} from "@grimoire/domain";
var initialFilters = {
  searchQuery: "",
  contentType: "all",
  personalState: "all",
  sourceHealth: "all",
  genre: "all",
  tag: "all",
  sortBy: "lastActive",
  sortOrder: "desc",
  viewMode: "grid"
};
function extractUniqueGenresAndTags(items) {
  const genres = /* @__PURE__ */ new Set();
  const tags = /* @__PURE__ */ new Set();
  for (const item of items) {
    if (item.work.deletedAt) continue;
    for (const g of item.work.genres) genres.add(g);
    for (const t of item.work.tags) tags.add(t);
  }
  return {
    allGenres: Array.from(genres).sort(),
    allTags: Array.from(tags).sort()
  };
}
var useLibraryStore = create((set, get) => {
  const initialItems = clientRepository.getItems();
  const { allGenres, allTags } = extractUniqueGenresAndTags(initialItems);
  return {
    ...initialFilters,
    items: initialItems,
    allGenres,
    allTags,
    refresh: () => {
      const items = clientRepository.getItems();
      const meta = extractUniqueGenresAndTags(items);
      set({ items, ...meta });
    },
    setSearchQuery: (query) => set({ searchQuery: query }),
    setContentType: (contentType) => set({ contentType }),
    setPersonalState: (personalState) => set({ personalState }),
    setSourceHealth: (sourceHealth) => set({ sourceHealth }),
    setGenre: (genre) => set({ genre }),
    setTag: (tag) => set({ tag }),
    setSortBy: (sortBy) => set({ sortBy }),
    setSortOrder: (sortOrder) => set({ sortOrder }),
    setViewMode: (viewMode) => set({ viewMode }),
    resetFilters: () => set({
      searchQuery: "",
      contentType: "all",
      personalState: "all",
      sourceHealth: "all",
      genre: "all",
      tag: "all",
      sortBy: "lastActive",
      sortOrder: "desc"
    }),
    createWork: (workParams, initialState, initialAnchor, initialPath) => {
      const created = clientRepository.createWork(
        workParams,
        initialState,
        initialAnchor,
        initialPath
      );
      get().refresh();
      return created;
    },
    updateWork: (id, patch) => {
      const updated = clientRepository.updateWork(id, patch);
      get().refresh();
      return updated;
    },
    updateRelationship: (workId, patch) => {
      const updated = clientRepository.updateRelationship(workId, patch);
      get().refresh();
      return updated;
    },
    deleteWork: (id) => {
      clientRepository.deleteWork(id);
      get().refresh();
    },
    addAccessPath: (workId, path) => {
      const accessPath = clientRepository.addAccessPath(workId, path);
      get().refresh();
      return accessPath;
    },
    recordProgress: (workId, anchor, method = ProgressMethod2.Reader) => {
      clientRepository.recordProgress(workId, anchor, method);
      get().refresh();
    }
  };
});
clientRepository.subscribeToStorage(() => {
  useLibraryStore.getState().refresh();
});
function selectFilteredWorks(state, options) {
  const includeVaultWorks = options?.includeVaultWorks ?? false;
  const {
    items,
    searchQuery,
    contentType,
    personalState,
    sourceHealth,
    genre,
    tag,
    sortBy,
    sortOrder
  } = state;
  return items.filter((item) => {
    if (item.work.deletedAt) return false;
    if (!includeVaultWorks && item.work.privacyState === "vault") {
      return false;
    }
    if (searchQuery.trim().length > 0) {
      const q = searchQuery.toLowerCase().trim();
      const matchesTitle = item.work.title.toLowerCase().includes(q);
      const matchesOriginalTitle = item.work.originalTitle ? item.work.originalTitle.toLowerCase().includes(q) : false;
      const matchesAlt = item.work.alternativeTitles.some((a) => a.toLowerCase().includes(q));
      const matchesAlias = item.work.aliases.some((a) => a.toLowerCase().includes(q));
      const matchesDesc = item.work.description ? item.work.description.toLowerCase().includes(q) : false;
      const matchesTag = item.work.tags.some((t) => t.toLowerCase().includes(q));
      const matchesGenre = item.work.genres.some((g) => g.toLowerCase().includes(q));
      const matchesCreator = (item.work.creators ?? []).some(
        (c) => c.name.toLowerCase().includes(q)
      );
      if (!matchesTitle && !matchesOriginalTitle && !matchesAlt && !matchesAlias && !matchesDesc && !matchesTag && !matchesGenre && !matchesCreator) {
        return false;
      }
    }
    if (contentType !== "all" && item.work.contentType !== contentType) {
      return false;
    }
    if (personalState !== "all") {
      if (!item.relationship || item.relationship.personalState !== personalState) {
        return false;
      }
    }
    if (sourceHealth !== "all") {
      const hasMatchingHealth = item.accessPaths.some((p) => p.health === sourceHealth);
      if (!hasMatchingHealth) return false;
    }
    if (genre !== "all" && !item.work.genres.includes(genre)) {
      return false;
    }
    if (tag !== "all" && !item.work.tags.includes(tag)) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case "title":
        comparison = a.work.title.localeCompare(b.work.title);
        break;
      case "updatedAt":
        comparison = new Date(b.work.updatedAt).getTime() - new Date(a.work.updatedAt).getTime();
        break;
      case "lastActive": {
        const aActive = a.relationship?.lastActiveAt ? new Date(a.relationship.lastActiveAt).getTime() : 0;
        const bActive = b.relationship?.lastActiveAt ? new Date(b.relationship.lastActiveAt).getTime() : 0;
        comparison = bActive - aActive;
        break;
      }
      case "progress": {
        const aAnchor = a.relationship?.currentAnchor;
        const bAnchor = b.relationship?.currentAnchor;
        const aVal = aAnchor && "chapter" in aAnchor ? aAnchor.chapter : 0;
        const bVal = bAnchor && "chapter" in bAnchor ? bAnchor.chapter : 0;
        comparison = bVal - aVal;
        break;
      }
    }
    return sortOrder === "asc" ? -comparison : comparison;
  });
}

// src/stores/use-return-desk-store.ts
import { create as create2 } from "zustand";
import {
  ReturnSignalType,
  computeReturnSignals
} from "@grimoire/domain";
function calculateDeskState() {
  const items = clientRepository.getItems();
  const itemMap = /* @__PURE__ */ new Map();
  const relationships = [];
  const updatesMap = /* @__PURE__ */ new Map();
  for (const item of items) {
    if (item.work.deletedAt) continue;
    itemMap.set(item.work.id, item);
    if (item.relationship) {
      relationships.push(item.relationship);
    }
    if (item.updates.length > 0) {
      updatesMap.set(item.work.id, item.updates);
    }
  }
  const signals = computeReturnSignals(relationships, updatesMap, /* @__PURE__ */ new Date());
  const continueRail = [];
  const catchUpList = [];
  const bingeReadyList = [];
  const stalledList = [];
  const waitingList = [];
  const rediscoverList = [];
  for (const signal of signals) {
    const item = itemMap.get(signal.workId);
    if (!item) continue;
    switch (signal.type) {
      case ReturnSignalType.ContinueNow:
        continueRail.push({ signal, item });
        break;
      case ReturnSignalType.CatchUp:
        catchUpList.push({ signal, item });
        break;
      case ReturnSignalType.BingeReady:
        bingeReadyList.push({ signal, item });
        break;
      case ReturnSignalType.Stalled:
        stalledList.push({ signal, item });
        break;
      case ReturnSignalType.Waiting:
        waitingList.push({ signal, item });
        break;
      case ReturnSignalType.Rediscover:
        rediscoverList.push({ signal, item });
        break;
    }
  }
  const heroSignal = signals[0] ?? null;
  const heroWork = heroSignal ? itemMap.get(heroSignal.workId) ?? null : null;
  return {
    allSignals: signals,
    heroSignal,
    heroWork,
    continueRail,
    catchUpList,
    bingeReadyList,
    stalledList,
    waitingList,
    rediscoverList
  };
}
var useReturnDeskStore = create2((set, get) => ({
  ...calculateDeskState(),
  selectedWorkId: null,
  selectedWorkItem: null,
  isRefresherOpen: false,
  refresh: () => {
    const desk = calculateDeskState();
    const currentSelectedId = get().selectedWorkId;
    const selectedWorkItem = currentSelectedId ? clientRepository.getItem(currentSelectedId) ?? null : null;
    set({
      ...desk,
      selectedWorkItem
    });
  },
  openRefresher: (workId) => {
    const item = clientRepository.getItem(workId) ?? null;
    set({
      selectedWorkId: workId,
      selectedWorkItem: item,
      isRefresherOpen: true
    });
  },
  closeRefresher: () => {
    set({
      isRefresherOpen: false,
      selectedWorkId: null,
      selectedWorkItem: null
    });
  }
}));

// src/stores/use-work-detail-store.ts
import { create as create3 } from "zustand";
import {
  ProgressMethod as ProgressMethod3,
  SourceHealth as SourceHealth3,
  applyHealthCheckResult
} from "@grimoire/domain";
var useWorkDetailStore = create3((set, get) => ({
  activeWorkId: null,
  activeItem: null,
  relations: [],
  isLoading: false,
  loadWork: (workId) => {
    set({ isLoading: true, activeWorkId: workId });
    const item = clientRepository.getItem(workId) ?? null;
    const relations = clientRepository.getWorkRelations(workId);
    set({ activeItem: item, relations, isLoading: false });
  },
  clearWork: () => {
    set({ activeWorkId: null, activeItem: null, relations: [], isLoading: false });
  },
  updateShelfTier: (tier) => {
    const { activeWorkId } = get();
    if (!activeWorkId) return;
    clientRepository.updateRelationship(activeWorkId, { shelfTier: tier });
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
  },
  updatePersonalState: (state, stoppedReason, stoppedNote) => {
    const { activeWorkId } = get();
    if (!activeWorkId) return;
    clientRepository.updatePersonalState(activeWorkId, state, stoppedReason, stoppedNote);
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
  },
  recordProgress: (newAnchor, method = ProgressMethod3.Manual) => {
    const { activeWorkId } = get();
    if (!activeWorkId) return;
    clientRepository.recordProgress(activeWorkId, newAnchor, method);
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
  },
  advanceChapter: (delta) => {
    const { activeItem, activeWorkId } = get();
    if (!activeWorkId || !activeItem) return;
    const currentAnchor = activeItem.relationship?.currentAnchor;
    let nextChapter = 1;
    if (currentAnchor && "chapter" in currentAnchor && typeof currentAnchor.chapter === "number") {
      nextChapter = Math.max(0, currentAnchor.chapter + delta);
    } else {
      nextChapter = Math.max(0, 1 + delta);
    }
    const newAnchor = {
      type: "chapter",
      chapter: nextChapter,
      title: `Chapter ${nextChapter}`
    };
    clientRepository.recordProgress(activeWorkId, newAnchor, ProgressMethod3.Manual);
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
  },
  addAccessPath: (path) => {
    const { activeWorkId } = get();
    if (!activeWorkId) return null;
    const created = clientRepository.addAccessPath(activeWorkId, path);
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
    return created;
  },
  deleteAccessPath: (pathId) => {
    const { activeWorkId } = get();
    if (!activeWorkId) return;
    clientRepository.deleteAccessPath(activeWorkId, pathId);
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
  },
  setPreferredAccessPath: (pathId) => {
    const { activeWorkId } = get();
    if (!activeWorkId) return;
    clientRepository.setPreferredAccessPath(activeWorkId, pathId);
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useLibraryStore.getState().refresh();
  },
  checkAccessPathHealth: async (pathId) => {
    const { activeWorkId, activeItem } = get();
    if (!activeWorkId || !activeItem) return SourceHealth3.Unknown;
    const path = activeItem.accessPaths.find((p) => p.id === pathId);
    if (!path) return SourceHealth3.Unknown;
    let probeOk = true;
    let status = 200;
    let errorMsg;
    if (path.kind === "local_file") {
      probeOk = Boolean(path.filePath && path.filePath.trim().length > 0);
    } else if (path.kind === "web" || path.kind === "feed") {
      const url = path.kind === "web" ? path.url : path.feedUrl;
      if (!url || !url.startsWith("http")) {
        probeOk = false;
        errorMsg = "Invalid URL scheme";
      } else if (typeof fetch !== "undefined") {
        try {
          const resp = await fetch(url, { method: "HEAD", mode: "no-cors" });
          probeOk = resp.type === "opaque" || resp.ok;
          status = resp.status || 200;
        } catch (e) {
          probeOk = false;
          errorMsg = e instanceof Error ? e.message : "Network probe failed";
        }
      }
    }
    const { monitorState } = applyHealthCheckResult(path, {
      ok: probeOk,
      status,
      errorMessage: errorMsg
    });
    clientRepository.updateAccessPathHealth(activeWorkId, pathId, monitorState);
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
    return monitorState.health;
  },
  addMemory: (memory) => {
    const { activeWorkId } = get();
    if (!activeWorkId) return null;
    const created = clientRepository.addMemory(activeWorkId, memory);
    const updated = clientRepository.getItem(activeWorkId) ?? null;
    set({ activeItem: updated });
    useReturnDeskStore.getState().refresh();
    return created;
  },
  archiveWork: (workId, reason, whyStoppedNote) => {
    const record = clientRepository.archiveWork(workId, reason, whyStoppedNote);
    const { activeWorkId } = get();
    if (activeWorkId === workId) {
      const updated = clientRepository.getItem(workId) ?? null;
      set({ activeItem: updated });
    }
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
    return record;
  },
  findDuplicates: (workId) => {
    return clientRepository.findDuplicates(workId);
  },
  executeMerge: (targetId, secondaryId, options) => {
    const merged = clientRepository.executeMerge(targetId, secondaryId, options);
    const { activeWorkId } = get();
    if (activeWorkId === targetId || activeWorkId === secondaryId) {
      const updated = clientRepository.getItem(targetId) ?? null;
      const relations = clientRepository.getWorkRelations(targetId);
      set({ activeItem: updated, activeWorkId: targetId, relations });
    }
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
    return merged;
  },
  loadRelations: (workId) => {
    const relations = clientRepository.getWorkRelations(workId);
    set({ relations });
  },
  linkWorkRelation: (sourceId, targetId, relationType, notes) => {
    const relation = clientRepository.linkWorkRelation(
      sourceId,
      targetId,
      relationType,
      notes
    );
    const { activeWorkId } = get();
    if (activeWorkId) {
      const relations = clientRepository.getWorkRelations(activeWorkId);
      set({ relations });
    }
    return relation;
  },
  removeWorkRelation: (relationId) => {
    clientRepository.removeWorkRelation(relationId);
    const { activeWorkId } = get();
    if (activeWorkId) {
      const relations = clientRepository.getWorkRelations(activeWorkId);
      set({ relations });
    }
  },
  updateCover: (coverRef) => {
    const { activeWorkId } = get();
    if (!activeWorkId) return;
    clientRepository.updateWork(activeWorkId, { coverRef });
    get().loadWork(activeWorkId);
    useLibraryStore.getState().refresh();
    useReturnDeskStore.getState().refresh();
  }
}));

// src/stores/use-radar-store.ts
import { create as create4 } from "zustand";
import { normalizeUrl as normalizeUrl2 } from "@grimoire/domain";

// src/radar-import.ts
import { normalizeUrl } from "@grimoire/domain";
var MAX_IMPORT_BYTES = 4 * 1024 * 1024;
var MAX_BATCH_ITEMS = 100;
var BROWSERS = /* @__PURE__ */ new Set(["chromium", "firefox", "edge", "safari", "unknown"]);
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function requiredString(value, field, maxLength) {
  if (typeof value !== "string" || value.trim().length === 0 || value.length > maxLength) {
    throw new Error(`Radar import has an invalid ${field}.`);
  }
  return value.trim();
}
function optionalNonNegativeInteger(value, field) {
  if (value === void 0 || value === null) return void 0;
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`Radar import has an invalid ${field}.`);
  }
  return value;
}
function isTimestamp(value) {
  return Number.isFinite(Date.parse(value)) && /^\d{4}-\d{2}-\d{2}T/.test(value);
}
var SENSITIVE_PARAMETER = /(^|[-_])(access[-_]?token|api[-_]?key|auth|authorization|cookie|jwt|password|passwd|refresh[-_]?token|secret|session(id|key)?|sid|token)([-_]|$)/i;
function sanitizeRadarCaptureUrl(rawUrl) {
  const parsed = new URL(rawUrl);
  if (parsed.username || parsed.password) {
    throw new Error("Radar import cannot contain URLs with embedded credentials.");
  }
  for (const key of Array.from(parsed.searchParams.keys())) {
    const normalizedKey = key.replace(/([a-z0-9])([A-Z])/g, "$1_$2");
    if (SENSITIVE_PARAMETER.test(normalizedKey)) {
      parsed.searchParams.delete(key);
    }
  }
  parsed.hash = "";
  const url = parsed.toString();
  const normalized = normalizeUrl(url);
  if (!normalized.isValid) throw new Error("Radar import contains a non-HTTP(S) URL.");
  return { url: normalized.normalizedUrl, normalizedUrl: normalized.normalizedUrl };
}
function parseRadarImport(json) {
  if (new TextEncoder().encode(json).byteLength > MAX_IMPORT_BYTES) {
    throw new Error("Radar import exceeds the 4 MB size limit.");
  }
  let decoded;
  try {
    decoded = JSON.parse(json);
  } catch {
    throw new Error("The selected file is not valid JSON.");
  }
  if (!isRecord(decoded) || decoded["format"] !== "grimoire-radar" || decoded["version"] !== 1) {
    throw new Error("The selected file is not a supported Radar capture export.");
  }
  const batchId = requiredString(decoded["batchId"], "batch ID", 128);
  const batchLabel = requiredString(decoded["batchLabel"], "batch label", 200);
  const browserSource = typeof decoded["browserSource"] === "string" && BROWSERS.has(decoded["browserSource"]) ? decoded["browserSource"] : "unknown";
  const capturedAt = requiredString(decoded["capturedAt"], "capture timestamp", 64);
  if (!isTimestamp(capturedAt)) throw new Error("Radar import has an invalid capture timestamp.");
  const rawItems = decoded["items"];
  if (!Array.isArray(rawItems) || rawItems.length === 0 || rawItems.length > MAX_BATCH_ITEMS) {
    throw new Error(`Radar imports must contain between 1 and ${MAX_BATCH_ITEMS} items.`);
  }
  const seenUrls = /* @__PURE__ */ new Map();
  const items = [];
  for (const candidate of rawItems) {
    if (!isRecord(candidate)) throw new Error("Radar import contains an invalid capture item.");
    const rawUrl = requiredString(candidate["url"], "capture URL", 4096);
    let safeUrl;
    try {
      safeUrl = sanitizeRadarCaptureUrl(rawUrl);
    } catch (error) {
      if (error instanceof Error && error.message.includes("embedded credentials")) throw error;
      throw new Error("Radar import contains a non-HTTP(S) URL.");
    }
    const { url, normalizedUrl } = safeUrl;
    const itemBrowser = typeof candidate["browserSource"] === "string" && BROWSERS.has(candidate["browserSource"]) ? candidate["browserSource"] : browserSource;
    const itemCapturedAt = typeof candidate["capturedAt"] === "string" && isTimestamp(candidate["capturedAt"]) ? candidate["capturedAt"] : capturedAt;
    const captureTime = Date.parse(itemCapturedAt);
    const previousCaptureTime = seenUrls.get(normalizedUrl);
    if (previousCaptureTime !== void 0 && Math.abs(captureTime - previousCaptureTime) <= 24 * 60 * 60 * 1e3) {
      continue;
    }
    items.push({
      id: requiredString(candidate["id"], "capture ID", 128),
      url,
      title: requiredString(candidate["title"], "capture title", 500),
      browserSource: itemBrowser,
      ...optionalNonNegativeInteger(candidate["windowId"], "window ID") !== void 0 ? { windowId: optionalNonNegativeInteger(candidate["windowId"], "window ID") } : {},
      ...optionalNonNegativeInteger(candidate["tabId"], "tab ID") !== void 0 ? { tabId: optionalNonNegativeInteger(candidate["tabId"], "tab ID") } : {},
      capturedAt: itemCapturedAt,
      batchId,
      batchLabel,
      normalizedUrl
    });
    seenUrls.set(normalizedUrl, captureTime);
  }
  if (items.length === 0) throw new Error("Radar import contains no usable capture URLs.");
  return {
    format: "grimoire-radar",
    version: 1,
    batchId,
    batchLabel,
    browserSource,
    capturedAt,
    items
  };
}

// src/stores/use-radar-store.ts
var STORAGE_KEY2 = "cv:radar-captures:v1";
var initialFilters2 = {
  state: "all",
  browserSource: "all",
  sortBy: "capturedAt",
  sortOrder: "desc",
  groupByWork: true
};
function isCaptureState(value) {
  return value === "pending" || value === "matched" || value === "created" || value === "merged" || value === "discarded" || value === "temporary";
}
function isBrowserSource(value) {
  return value === "chromium" || value === "firefox" || value === "edge" || value === "safari" || value === "unknown";
}
function isStoredItem(value) {
  if (typeof value !== "object" || value === null) return false;
  const item = value;
  if (typeof item["url"] !== "string") return false;
  try {
    if (sanitizeRadarCaptureUrl(item["url"]).url !== item["url"]) return false;
  } catch {
    return false;
  }
  return typeof item["id"] === "string" && typeof item["title"] === "string" && typeof item["capturedAt"] === "string" && isCaptureState(item["state"]) && isBrowserSource(item["browserSource"]);
}
function loadItems() {
  if (typeof localStorage === "undefined") return { items: [], error: null };
  try {
    const raw = localStorage.getItem(STORAGE_KEY2);
    if (raw === null) {
      persistItems([]);
      return { items: [], error: null };
    }
    const decoded = JSON.parse(raw);
    if (!Array.isArray(decoded) || !decoded.every(isStoredItem)) {
      throw new Error("Saved Radar captures have an invalid format.");
    }
    return { items: decoded, error: null };
  } catch (error) {
    return {
      items: [],
      error: error instanceof Error ? error.message : "Unable to read saved Radar captures."
    };
  }
}
function persistItems(items) {
  if (typeof localStorage === "undefined") return null;
  try {
    localStorage.setItem(STORAGE_KEY2, JSON.stringify(items));
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : "Unable to save Radar captures.";
  }
}
function withUpdatedItems(items) {
  return {
    items,
    pendingCount: items.filter((item) => item.state === "pending").length,
    storageError: persistItems(items)
  };
}
var saved = loadItems();
var useRadarStore = create4((set) => ({
  ...initialFilters2,
  items: saved.items,
  pendingCount: saved.items.filter((item) => item.state === "pending").length,
  storageError: saved.error,
  refresh: () => {
    const current = loadItems();
    set({
      ...current,
      pendingCount: current.items.filter((item) => item.state === "pending").length
    });
  },
  setState: (state) => set({ state }),
  setBrowserSource: (browserSource) => set({ browserSource }),
  setSortBy: (sortBy) => set({ sortBy }),
  setSortOrder: (sortOrder) => set({ sortOrder }),
  setGroupByWork: (groupByWork) => set({ groupByWork }),
  resetFilters: () => set(initialFilters2),
  importBatch: (batch) => {
    let imported = 0;
    let duplicates = 0;
    set((state) => {
      const normalizedExisting = new Set(
        state.items.map((item) => item.normalizedUrl ?? normalizeUrl2(item.url).normalizedUrl)
      );
      const ids = new Set(state.items.map((item) => item.id));
      const additions = [];
      for (const incoming of batch.items) {
        const incomingTime = Date.parse(incoming.capturedAt);
        const recentDuplicate = normalizedExisting.has(incoming.normalizedUrl) && state.items.some((item) => {
          const existingUrl = item.normalizedUrl ?? normalizeUrl2(item.url).normalizedUrl;
          return existingUrl === incoming.normalizedUrl && Math.abs(incomingTime - Date.parse(item.capturedAt)) <= 24 * 60 * 60 * 1e3;
        });
        if (ids.has(incoming.id) || recentDuplicate) {
          duplicates++;
          continue;
        }
        additions.push({
          id: incoming.id,
          url: incoming.url,
          normalizedUrl: incoming.normalizedUrl,
          title: incoming.title,
          faviconUrl: incoming.faviconUrl,
          browserSource: incoming.browserSource,
          windowId: incoming.windowId,
          tabId: incoming.tabId,
          capturedAt: incoming.capturedAt,
          batchId: incoming.batchId,
          batchLabel: incoming.batchLabel,
          state: "pending"
        });
        ids.add(incoming.id);
        normalizedExisting.add(incoming.normalizedUrl);
        imported++;
      }
      return withUpdatedItems([...additions, ...state.items]);
    });
    return { imported, duplicates };
  },
  matchToWork: (itemId, workId, workTitle) => set(
    (state) => withUpdatedItems(
      state.items.map(
        (item) => item.id === itemId ? {
          ...item,
          state: "matched",
          matchedWorkId: workId,
          matchedWorkTitle: workTitle,
          matchConfidence: 1
        } : item
      )
    )
  ),
  createNewWork: (itemId, workId, workTitle) => set(
    (state) => withUpdatedItems(
      state.items.map(
        (item) => item.id === itemId ? {
          ...item,
          state: "created",
          matchedWorkId: workId,
          matchedWorkTitle: workTitle,
          matchConfidence: 1
        } : item
      )
    )
  ),
  keepTemporary: (itemId) => set(
    (state) => withUpdatedItems(
      state.items.map((item) => item.id === itemId ? { ...item, state: "temporary" } : item)
    )
  ),
  discardItem: (itemId) => set(
    (state) => withUpdatedItems(
      state.items.map((item) => item.id === itemId ? { ...item, state: "discarded" } : item)
    )
  ),
  bulkDiscard: (itemIds) => {
    const ids = new Set(itemIds);
    set(
      (state) => withUpdatedItems(
        state.items.map((item) => ids.has(item.id) ? { ...item, state: "discarded" } : item)
      )
    );
  },
  bulkMatchToWork: (itemIds, workId, workTitle) => {
    const ids = new Set(itemIds);
    set(
      (state) => withUpdatedItems(
        state.items.map(
          (item) => ids.has(item.id) ? {
            ...item,
            state: "matched",
            matchedWorkId: workId,
            matchedWorkTitle: workTitle,
            matchConfidence: 1
          } : item
        )
      )
    );
  }
}));
function selectFilteredRadarItems(state) {
  const { items, state: filterState, browserSource, sortBy, sortOrder } = state;
  return items.filter((item) => {
    if (filterState !== "all" && item.state !== filterState) return false;
    if (browserSource !== "all" && item.browserSource !== browserSource) return false;
    return true;
  }).sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case "capturedAt":
        comparison = new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime();
        break;
      case "matchConfidence":
        comparison = (b.matchConfidence ?? 0) - (a.matchConfidence ?? 0);
        break;
      case "title":
        comparison = a.title.localeCompare(b.title);
        break;
    }
    return sortOrder === "asc" ? -comparison : comparison;
  });
}
function selectGroupedByWork(items) {
  const groups = /* @__PURE__ */ new Map();
  for (const item of items) {
    const key = item.matchedWorkId ?? null;
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  }
  return groups;
}

// src/stores/use-work-relations-store.ts
import { create as create5 } from "zustand";
var useWorkRelationsStore = create5((set, get) => ({
  relations: [],
  isLoading: false,
  loadRelations: (workId) => {
    set({ isLoading: true });
    const relations = clientRepository.getWorkRelations(workId);
    set({ relations, isLoading: false });
  },
  linkWorkRelation: (sourceId, targetId, relationType, notes) => {
    const relation = clientRepository.linkWorkRelation(
      sourceId,
      targetId,
      relationType,
      notes
    );
    const current = get().relations;
    if (!current.some((r) => r.id === relation.id)) {
      set({ relations: [...current, relation] });
    }
    return relation;
  },
  removeWorkRelation: (relationId) => {
    clientRepository.removeWorkRelation(relationId);
    set((state) => ({
      relations: state.relations.filter((r) => r.id !== relationId)
    }));
  }
}));

// src/stores/use-merge-wizard-store.ts
import { create as create6 } from "zustand";
var useMergeWizardStore = create6((set, get) => ({
  isOpen: false,
  targetWorkId: null,
  secondaryWorkId: null,
  candidates: [],
  isSearching: false,
  isMerging: false,
  openWizard: (targetWorkId) => {
    set({ isOpen: true, targetWorkId, secondaryWorkId: null, isSearching: true });
    const candidates = clientRepository.findDuplicates(targetWorkId);
    set({ candidates, isSearching: false });
  },
  closeWizard: () => {
    set({ isOpen: false, targetWorkId: null, secondaryWorkId: null, candidates: [] });
  },
  setSecondaryWorkId: (workId) => {
    set({ secondaryWorkId: workId });
  },
  findDuplicates: (workId) => {
    set({ isSearching: true });
    const candidates = clientRepository.findDuplicates(workId);
    set({ candidates, isSearching: false });
    return candidates;
  },
  executeMerge: (targetId, secondaryId, options) => {
    set({ isMerging: true });
    try {
      const merged = clientRepository.executeMerge(targetId, secondaryId, options);
      useLibraryStore.getState().refresh();
      useReturnDeskStore.getState().refresh();
      if (useWorkDetailStore.getState().activeWorkId === targetId) {
        useWorkDetailStore.getState().loadWork(targetId);
      }
      set({ isMerging: false, isOpen: false, secondaryWorkId: null });
      return merged;
    } catch (err) {
      set({ isMerging: false });
      throw err;
    }
  }
}));

// src/stores/use-categories-store.ts
import { create as create7 } from "zustand";
var STORAGE_KEY3 = "cv:custom_categories:v1";
function getInitialCategories() {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(STORAGE_KEY3);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
    }
  }
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return [
    {
      id: "cat-masterpieces",
      name: "Must-Read Masterpieces",
      description: "Stories of transcendent narrative ambition, worldbuilding, and emotional weight.",
      color: "indigo",
      workIds: [],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "cat-comfort",
      name: "Late Night Comfort",
      description: "Warm, low-stress, relaxing comfort reads to unwind after a long day.",
      color: "amber",
      workIds: [],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "cat-survival",
      name: "High Stakes Survival",
      description: "Brutal death games, unforgiving battlefields, and razor-thin margins of life or death.",
      color: "rose",
      workIds: [],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "cat-progression",
      name: "Progression Fantasy",
      description: "Satisfying power scaling, cultivation systems, tower climbs, and relentless self-improvement.",
      color: "emerald",
      workIds: [],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "cat-peak",
      name: "Peak Fiction",
      description: "Peak execution across character arcs, unforgettable dialogue, and shocking twists.",
      color: "sky",
      workIds: [],
      createdAt: now,
      updatedAt: now
    }
  ];
}
function persistCategories(categories) {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY3, JSON.stringify(categories));
    } catch {
    }
  }
}
var useCategoriesStore = create7((set, get) => {
  const initial = getInitialCategories();
  return {
    categories: initial,
    selectedCategoryId: initial[0]?.id ?? null,
    setSelectedCategoryId: (id) => set({ selectedCategoryId: id }),
    createCategory: ({ name, description, color, initialWorkIds = [] }) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const newCat = {
        id: `cat-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: name.trim(),
        description: description.trim(),
        color,
        workIds: initialWorkIds,
        createdAt: now,
        updatedAt: now
      };
      const updated = [...get().categories, newCat];
      persistCategories(updated);
      set({ categories: updated, selectedCategoryId: newCat.id });
      return newCat;
    },
    updateCategory: (id, patch) => {
      const updated = get().categories.map((cat) => {
        if (cat.id !== id) return cat;
        return {
          ...cat,
          ...patch,
          updatedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
      });
      persistCategories(updated);
      set({ categories: updated });
    },
    deleteCategory: (id) => {
      const current = get().categories;
      const updated = current.filter((c) => c.id !== id);
      persistCategories(updated);
      const nextSelected = get().selectedCategoryId === id ? updated[0]?.id ?? null : get().selectedCategoryId;
      set({ categories: updated, selectedCategoryId: nextSelected });
    },
    assignWorkToCategory: (categoryId, workId) => {
      const updated = get().categories.map((cat) => {
        if (cat.id !== categoryId) return cat;
        if (cat.workIds.includes(workId)) return cat;
        return {
          ...cat,
          workIds: [],
          updatedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
      });
      persistCategories(updated);
      set({ categories: updated });
    },
    removeWorkFromCategory: (categoryId, workId) => {
      const updated = get().categories.map((cat) => {
        if (cat.id !== categoryId) return cat;
        if (!cat.workIds.includes(workId)) return cat;
        return {
          ...cat,
          workIds: cat.workIds.filter((id) => id !== workId),
          updatedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
      });
      persistCategories(updated);
      set({ categories: updated });
    },
    toggleWorkInCategory: (categoryId, workId) => {
      const cat = get().categories.find((c) => c.id === categoryId);
      if (!cat) return;
      if (cat.workIds.includes(workId)) {
        get().removeWorkFromCategory(categoryId, workId);
      } else {
        get().assignWorkToCategory(categoryId, workId);
      }
    },
    isWorkInCategory: (categoryId, workId) => {
      const cat = get().categories.find((c) => c.id === categoryId);
      return cat ? cat.workIds.includes(workId) : false;
    },
    getCategoriesForWork: (workId) => {
      return get().categories.filter((cat) => cat.workIds.includes(workId));
    },
    refresh: () => {
      set({ categories: getInitialCategories() });
    }
  };
});

// src/stores/use-lists-store.ts
import { create as create8 } from "zustand";
import {
  ContentOrigin as ContentOrigin2,
  PublicationState as PublicationState2,
  PrivacyState as PrivacyState2,
  PersonalState as PersonalState4
} from "@grimoire/domain";
var STORAGE_KEY4 = "cv:user_lists:v1";
function getInitialPersonalLists() {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(STORAGE_KEY4);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
    }
  }
  return [];
}
function persistPersonalLists(lists) {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY4, JSON.stringify(lists));
    } catch {
    }
  }
}
var COMMUNITY_LISTS_SEED = [];
var PUBLICATIONS_STORAGE_KEY = "cv:publications:v1";
var MAX_PUBLICATION_RECORDS = 50;
function getInitialPublications() {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(PUBLICATIONS_STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {
    }
  }
  return [];
}
function persistPublications(records) {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(PUBLICATIONS_STORAGE_KEY, JSON.stringify(records));
    } catch {
    }
  }
}
var useListsStore = create8((set, get) => {
  const initialPersonal = getInitialPersonalLists();
  return {
    myLists: initialPersonal,
    selectedListId: initialPersonal[0]?.id ?? null,
    communityLists: COMMUNITY_LISTS_SEED,
    communitySearchQuery: "",
    communityGenreFilter: "All",
    publications: getInitialPublications(),
    setSelectedListId: (id) => set({ selectedListId: id }),
    createList: ({ title, description, privacyState = PrivacyState2.Public }) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const newList = {
        id: `list-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        title: title.trim(),
        description: description?.trim() || null,
        privacyState,
        items: [],
        createdAt: now,
        updatedAt: now
      };
      const updated = [...get().myLists, newList];
      persistPersonalLists(updated);
      set({ myLists: updated, selectedListId: newList.id });
      return newList;
    },
    updateList: (id, patch) => {
      const updated = get().myLists.map((list) => {
        if (list.id !== id) return list;
        return {
          ...list,
          ...patch,
          updatedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
      });
      persistPersonalLists(updated);
      set({ myLists: updated });
    },
    deleteList: (id) => {
      get().softDeleteList(id);
    },
    softDeleteList: (id) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const updated = get().myLists.map((l) => l.id === id ? { ...l, deletedAt: now, updatedAt: now } : l);
      persistPersonalLists(updated);
      const activeLists = updated.filter((l) => !l.deletedAt);
      const nextSelected = get().selectedListId === id ? activeLists[0]?.id ?? null : get().selectedListId;
      set({ myLists: updated, selectedListId: nextSelected });
    },
    restoreList: (id) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const updated = get().myLists.map((l) => l.id === id ? { ...l, deletedAt: null, updatedAt: now } : l);
      persistPersonalLists(updated);
      set({ myLists: updated });
    },
    purgeList: (id) => {
      const updated = get().myLists.filter((l) => l.id !== id);
      persistPersonalLists(updated);
      const activeLists = updated.filter((l) => !l.deletedAt);
      const nextSelected = get().selectedListId === id ? activeLists[0]?.id ?? null : get().selectedListId;
      set({ myLists: updated, selectedListId: nextSelected });
    },
    getDeletedLists: () => {
      return get().myLists.filter((l) => Boolean(l.deletedAt));
    },
    addWorkToList: (listId, workId, note) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const updated = get().myLists.map((list) => {
        if (list.id !== listId) return list;
        if (list.items.some((item) => item.workId === workId)) return list;
        const newItem = {
          id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          workId,
          position: list.items.length,
          personalNote: note ?? null,
          createdAt: now
        };
        return {
          ...list,
          items: [...list.items, newItem],
          updatedAt: now
        };
      });
      persistPersonalLists(updated);
      set({ myLists: updated });
    },
    removeWorkFromList: (listId, workId) => {
      const updated = get().myLists.map((list) => {
        if (list.id !== listId) return list;
        return {
          ...list,
          items: list.items.filter((item) => item.workId !== workId),
          updatedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
      });
      persistPersonalLists(updated);
      set({ myLists: updated });
    },
    moveListItem: (listId, itemId, direction) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const updated = get().myLists.map((list) => {
        if (list.id !== listId) return list;
        const ordered = [...list.items].sort((a, b) => a.position - b.position);
        const index = ordered.findIndex((item) => item.id === itemId);
        const targetIndex = direction === "up" ? index - 1 : index + 1;
        if (index < 0 || targetIndex < 0 || targetIndex >= ordered.length) return list;
        const moving = ordered[index];
        const other = ordered[targetIndex];
        if (!moving || !other) return list;
        ordered[index] = other;
        ordered[targetIndex] = moving;
        return {
          ...list,
          items: ordered.map((item, position) => ({ ...item, position })),
          updatedAt: now
        };
      });
      persistPersonalLists(updated);
      set({ myLists: updated });
    },
    setListItemNote: (listId, itemId, note) => {
      const trimmed = note?.trim() ? note.trim().slice(0, 500) : null;
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const updated = get().myLists.map((list) => {
        if (list.id !== listId) return list;
        return {
          ...list,
          items: list.items.map(
            (item) => item.id === itemId ? { ...item, personalNote: trimmed } : item
          ),
          updatedAt: now
        };
      });
      persistPersonalLists(updated);
      set({ myLists: updated });
    },
    recordPublication: (record) => {
      const entry = {
        ...record,
        id: `pub-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      };
      const updated = [entry, ...get().publications].slice(0, MAX_PUBLICATION_RECORDS);
      persistPublications(updated);
      set({ publications: updated });
    },
    isWorkInList: (listId, workId) => {
      const list = get().myLists.find((l) => l.id === listId);
      return list ? list.items.some((item) => item.workId === workId) : false;
    },
    setCommunitySearchQuery: (query) => set({ communitySearchQuery: query }),
    setCommunityGenreFilter: (genre) => set({ communityGenreFilter: genre }),
    cloneCommunityList: (communityListId) => {
      const community = get().communityLists.find((c) => c.id === communityListId);
      if (!community) {
        throw new Error("Community list not found");
      }
      const existingItems = clientRepository.getItems();
      let importedCount = 0;
      const workIds = [];
      for (const entry of community.items) {
        const found = existingItems.find(
          (item) => item.work.title.toLowerCase() === entry.title.toLowerCase()
        );
        if (found) {
          workIds.push(found.work.id);
        } else {
          const newWork = clientRepository.createWork(
            {
              title: entry.title,
              alternativeTitles: [],
              aliases: [],
              description: entry.synopsis ?? entry.curatorNote ?? null,
              contentType: entry.contentType,
              origin: entry.origin ?? ContentOrigin2.Unknown,
              publicationState: PublicationState2.Unknown,
              privacyState: PrivacyState2.Public,
              coverRef: entry.coverRef ?? null,
              externalIds: [],
              genres: entry.genres,
              tags: community.tags
            },
            PersonalState4.Planned,
            { type: "chapter", chapter: 1, title: "Chapter 1" }
          );
          workIds.push(newWork.work.id);
          importedCount++;
        }
      }
      useLibraryStore.getState().refresh();
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const newList = {
        id: `list-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        title: `${community.title} (Cloned)`,
        description: `Imported from community curation by ${community.curator.name} (${community.curator.handle})`,
        privacyState: PrivacyState2.Public,
        items: workIds.map((wId, idx) => ({
          id: `item-${Date.now()}-${idx}`,
          workId: wId,
          position: idx,
          personalNote: community.items[idx]?.curatorNote ?? null,
          createdAt: now
        })),
        createdAt: now,
        updatedAt: now
      };
      const updated = [...get().myLists, newList];
      persistPersonalLists(updated);
      set({ myLists: updated, selectedListId: newList.id });
      return { list: newList, importedCount };
    },
    addCommunityWorkToLibrary: (entry) => {
      const existingItems = clientRepository.getItems();
      const found = existingItems.find(
        (item) => item.work.title.toLowerCase() === entry.title.toLowerCase()
      );
      if (found) {
        return found.work.id;
      }
      const newWork = clientRepository.createWork(
        {
          title: entry.title,
          alternativeTitles: [],
          aliases: [],
          description: entry.synopsis ?? entry.curatorNote ?? null,
          contentType: entry.contentType,
          origin: entry.origin ?? ContentOrigin2.Unknown,
          publicationState: PublicationState2.Unknown,
          privacyState: PrivacyState2.Public,
          coverRef: entry.coverRef ?? null,
          externalIds: [],
          genres: entry.genres,
          tags: []
        },
        PersonalState4.Planned,
        { type: "chapter", chapter: 1, title: "Chapter 1" }
      );
      useLibraryStore.getState().refresh();
      return newWork.work.id;
    },
    refresh: () => {
      set({ myLists: getInitialPersonalLists() });
    }
  };
});

// src/stores/use-updates-store.ts
import { create as create9 } from "zustand";
var STORAGE_KEY5 = "cv:updates_state:v1";
function loadPersisted() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY5);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          seenIds: Array.isArray(parsed.seenIds) ? parsed.seenIds : [],
          dismissedIds: Array.isArray(parsed.dismissedIds) ? parsed.dismissedIds : [],
          snoozedUntil: parsed.snoozedUntil && typeof parsed.snoozedUntil === "object" ? parsed.snoozedUntil : {}
        };
      }
    } catch {
    }
  }
  return { seenIds: [], dismissedIds: [], snoozedUntil: {} };
}
function savePersisted(state) {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(STORAGE_KEY5, JSON.stringify(state));
    } catch {
    }
  }
}
function buildUpdateFeedId(workId, detectedAt, label) {
  return `${workId}:${detectedAt}:${label}`;
}
function collectLibraryUpdateIds(items, nowMs = Date.now()) {
  const ids = [];
  for (const item of items) {
    if (item.work.deletedAt || item.work.privacyState === "vault") continue;
    for (const update of item.updates) {
      const detectedMs = Date.parse(update.detectedAt);
      if (Number.isFinite(detectedMs) && detectedMs <= nowMs) {
        ids.push(buildUpdateFeedId(item.work.id, update.detectedAt, update.label));
      }
    }
  }
  return ids;
}
var DEFAULT_SNOOZE_MS = 24 * 60 * 60 * 1e3;
var useUpdatesStore = create9((set, get) => {
  const initial = loadPersisted();
  return {
    seenIds: initial.seenIds,
    dismissedIds: initial.dismissedIds,
    snoozedUntil: initial.snoozedUntil,
    markSeen: (updateId) => {
      const current = get();
      if (current.seenIds.includes(updateId)) return;
      const seenIds = [...current.seenIds, updateId];
      savePersisted({
        seenIds,
        dismissedIds: current.dismissedIds,
        snoozedUntil: current.snoozedUntil
      });
      set({ seenIds });
    },
    markAllSeen: (updateIds) => {
      const current = get();
      const merged = Array.from(/* @__PURE__ */ new Set([...current.seenIds, ...updateIds]));
      savePersisted({
        seenIds: merged,
        dismissedIds: current.dismissedIds,
        snoozedUntil: current.snoozedUntil
      });
      set({ seenIds: merged });
    },
    dismissUpdate: (updateId) => {
      const current = get();
      const dismissedIds = Array.from(/* @__PURE__ */ new Set([...current.dismissedIds, updateId]));
      const seenIds = Array.from(/* @__PURE__ */ new Set([...current.seenIds, updateId]));
      savePersisted({
        seenIds,
        dismissedIds,
        snoozedUntil: current.snoozedUntil
      });
      set({ dismissedIds, seenIds });
    },
    restoreDismissed: (updateId) => {
      const current = get();
      const dismissedIds = current.dismissedIds.filter((id) => id !== updateId);
      savePersisted({
        seenIds: current.seenIds,
        dismissedIds,
        snoozedUntil: current.snoozedUntil
      });
      set({ dismissedIds });
    },
    snoozeUpdate: (updateId, durationMs = DEFAULT_SNOOZE_MS, nowMs = Date.now()) => {
      const current = get();
      const untilIso = new Date(nowMs + durationMs).toISOString();
      const snoozedUntil = { ...current.snoozedUntil, [updateId]: untilIso };
      const seenIds = Array.from(/* @__PURE__ */ new Set([...current.seenIds, updateId]));
      savePersisted({
        seenIds,
        dismissedIds: current.dismissedIds,
        snoozedUntil
      });
      set({ snoozedUntil, seenIds });
    },
    unsnoozeUpdate: (updateId) => {
      const current = get();
      const snoozedUntil = { ...current.snoozedUntil };
      delete snoozedUntil[updateId];
      savePersisted({
        seenIds: current.seenIds,
        dismissedIds: current.dismissedIds,
        snoozedUntil
      });
      set({ snoozedUntil });
    },
    isUpdateActive: (updateId, nowMs = Date.now()) => {
      const current = get();
      if (current.dismissedIds.includes(updateId)) return false;
      const snoozeIso = current.snoozedUntil[updateId];
      if (snoozeIso) {
        const untilMs = Date.parse(snoozeIso);
        if (Number.isFinite(untilMs) && untilMs > nowMs) {
          return false;
        }
      }
      return true;
    },
    isUpdateSeen: (updateId) => {
      return get().seenIds.includes(updateId);
    },
    getUnreadCount: (items, nowMs = Date.now()) => {
      const current = get();
      const allIds = collectLibraryUpdateIds(items, nowMs);
      let unread = 0;
      for (const id of allIds) {
        if (!current.isUpdateActive(id, nowMs)) continue;
        if (!current.seenIds.includes(id)) {
          unread++;
        }
      }
      return unread;
    }
  };
});

// src/stores/use-reading-queue-store.ts
import { create as create10 } from "zustand";
import { PersonalState as PersonalState5 } from "@grimoire/domain";
var STORAGE_KEY6 = "cv:reading_queue:v1";
function loadInitialQueue() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY6);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.items)) {
          return {
            items: parsed.items,
            isPaused: Boolean(parsed.isPaused)
          };
        }
      }
    } catch {
    }
  }
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const seedItems = clientRepository.getItems().filter(
    (item) => item.relationship?.personalState === PersonalState5.Planned || item.relationship?.personalState === PersonalState5.Waiting
  ).slice(0, 3).map((item, idx) => ({
    id: `queue-seed-${idx + 1}`,
    workId: item.work.id,
    position: idx,
    pinned: idx === 0,
    targetDate: null,
    estimatedMinutes: 45 + idx * 30,
    addedAt: now
  }));
  return { items: seedItems, isPaused: false };
}
function persistQueue(state) {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(STORAGE_KEY6, JSON.stringify(state));
    } catch {
    }
  }
}
function normalizePositions(items) {
  const pinned = items.filter((i) => i.pinned).sort((a, b) => a.position - b.position);
  const unpinned = items.filter((i) => !i.pinned).sort((a, b) => a.position - b.position);
  return [...pinned, ...unpinned].map((item, index) => ({
    ...item,
    position: index
  }));
}
var useReadingQueueStore = create10((set, get) => {
  const initial = loadInitialQueue();
  const togglePin = (workId) => {
    const current = get();
    const toggled = current.items.map(
      (item) => item.workId === workId ? { ...item, pinned: !item.pinned } : item
    );
    const items = normalizePositions(toggled);
    persistQueue({ items, isPaused: current.isPaused });
    set({ items });
  };
  const setTargetDate = (workId, targetDate) => {
    const current = get();
    const items = current.items.map(
      (item) => item.workId === workId ? { ...item, targetDate } : item
    );
    persistQueue({ items, isPaused: current.isPaused });
    set({ items });
  };
  const setEstimatedMinutes = (workId, estimatedMinutes) => {
    const current = get();
    const items = current.items.map(
      (item) => item.workId === workId ? { ...item, estimatedMinutes } : item
    );
    persistQueue({ items, isPaused: current.isPaused });
    set({ items });
  };
  const togglePaused = () => {
    const current = get();
    const isPaused = !current.isPaused;
    persistQueue({ items: current.items, isPaused });
    set({ isPaused });
  };
  const suggestNextWorks = (libraryItems) => {
    const source = libraryItems ?? clientRepository.getItems();
    const queuedIds = new Set(get().items.map((i) => i.workId));
    const tierWeight = (tier) => {
      if (tier === "priority_watch") return 3;
      if (tier === "favorite") return 2;
      if (tier === "highlight") return 1;
      return 0;
    };
    return source.filter(
      (item) => !item.work.deletedAt && item.work.privacyState !== "vault" && !queuedIds.has(item.work.id) && (item.relationship?.personalState === PersonalState5.Planned || item.relationship?.personalState === PersonalState5.Paused || item.relationship?.personalState === PersonalState5.Stalled || item.relationship?.personalState === PersonalState5.Waiting || item.relationship?.personalState === PersonalState5.Reading)
    ).sort(
      (a, b) => tierWeight(b.relationship?.shelfTier) - tierWeight(a.relationship?.shelfTier)
    ).slice(0, 6);
  };
  return {
    items: normalizePositions(initial.items),
    isPaused: initial.isPaused,
    addToQueue: (workId, options) => {
      const current = get();
      if (current.items.some((i) => i.workId === workId)) return;
      const newItem = {
        id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        workId,
        position: current.items.length,
        pinned: Boolean(options?.pinned),
        targetDate: options?.targetDate ?? null,
        estimatedMinutes: options?.estimatedMinutes ?? 60,
        addedAt: (/* @__PURE__ */ new Date()).toISOString()
      };
      const items = normalizePositions([...current.items, newItem]);
      persistQueue({ items, isPaused: current.isPaused });
      set({ items });
    },
    removeFromQueue: (workId) => {
      const current = get();
      const items = normalizePositions(current.items.filter((i) => i.workId !== workId));
      persistQueue({ items, isPaused: current.isPaused });
      set({ items });
    },
    moveQueueItem: (workId, direction) => {
      const current = get();
      const ordered = [...current.items].sort((a, b) => a.position - b.position);
      const index = ordered.findIndex((i) => i.workId === workId);
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (index < 0 || targetIndex < 0 || targetIndex >= ordered.length) return;
      const moving = ordered[index];
      const other = ordered[targetIndex];
      if (!moving || !other) return;
      ordered[index] = other;
      ordered[targetIndex] = moving;
      const items = ordered.map((item, position) => ({ ...item, position }));
      persistQueue({ items, isPaused: current.isPaused });
      set({ items });
    },
    togglePin,
    togglePinQueueItem: togglePin,
    setTargetDate,
    setQueueTargetDate: setTargetDate,
    setEstimatedMinutes,
    setQueueEstimatedMinutes: setEstimatedMinutes,
    togglePaused,
    toggleQueuePaused: togglePaused,
    suggestNextWorks,
    getSuggestedWorks: suggestNextWorks
  };
});

// src/notebook-fandom-store.ts
import { create as create11 } from "zustand";
import {
  MemoryType as MemoryType2,
  PersonalState as PersonalState6,
  PrivacyState as PrivacyState3,
  SpoilerLevel as SpoilerLevel2
} from "@grimoire/domain";
var NOTEBOOK_STORAGE_KEY = "cv_work_notebooks_v1";
function createInitialNotebookSeed() {
  return {
    activatedWorkIds: [],
    entries: []
  };
}
function loadNotebookStorage() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(NOTEBOOK_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.activatedWorkIds) && Array.isArray(parsed.entries)) {
          return parsed;
        }
      }
    } catch {
    }
  }
  return createInitialNotebookSeed();
}
function saveNotebookStorage(data) {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(NOTEBOOK_STORAGE_KEY, JSON.stringify(data));
    } catch {
    }
  }
}
var useNotebookStore = create11((set, get) => {
  const initial = loadNotebookStorage();
  return {
    activatedWorkIds: initial.activatedWorkIds,
    entries: initial.entries,
    isActivated: (workId) => get().activatedWorkIds.includes(workId),
    activateNotebook: (workId) => {
      const current = get().activatedWorkIds;
      if (current.includes(workId)) return;
      const next = [...current, workId];
      set({ activatedWorkIds: next });
      saveNotebookStorage({ activatedWorkIds: next, entries: get().entries });
    },
    deactivateNotebook: (workId) => {
      const next = get().activatedWorkIds.filter((id) => id !== workId);
      set({ activatedWorkIds: next });
      saveNotebookStorage({ activatedWorkIds: next, entries: get().entries });
    },
    getEntriesForWork: (workId) => {
      return get().entries.filter((e) => e.workId === workId).sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return b.updatedAt.localeCompare(a.updatedAt);
      });
    },
    addEntry: (input) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const trimmedTitle = input.title.trim() || "Untitled scratch note";
      const trimmedBody = input.body.trim();
      const isPinned = Boolean(input.pinned);
      const existing = get().entries.find(
        (e) => e.workId === input.workId && e.title.toLowerCase() === trimmedTitle.toLowerCase()
      );
      if (existing) {
        if (input.pinned !== void 0 && existing.pinned !== isPinned) {
          get().updateEntry(existing.id, {
            pinned: isPinned,
            body: trimmedBody,
            chapterHint: input.chapterHint?.trim() || null
          });
        }
        return existing;
      }
      const entry = {
        id: `nb-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        workId: input.workId,
        category: input.category,
        title: trimmedTitle,
        body: trimmedBody,
        chapterHint: input.chapterHint?.trim() || null,
        pinned: isPinned,
        promotedMemoryId: null,
        createdAt: now,
        updatedAt: now
      };
      const activated = get().activatedWorkIds.includes(input.workId) ? get().activatedWorkIds : [...get().activatedWorkIds, input.workId];
      const nextEntries = [entry, ...get().entries];
      set({ activatedWorkIds: activated, entries: nextEntries });
      saveNotebookStorage({ activatedWorkIds: activated, entries: nextEntries });
      return entry;
    },
    updateEntry: (entryId, patch) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const nextEntries = get().entries.map(
        (e) => e.id === entryId ? { ...e, ...patch, updatedAt: now } : e
      );
      set({ entries: nextEntries });
      saveNotebookStorage({ activatedWorkIds: get().activatedWorkIds, entries: nextEntries });
    },
    deleteEntry: (entryId) => {
      const nextEntries = get().entries.filter((e) => e.id !== entryId);
      set({ entries: nextEntries });
      saveNotebookStorage({ activatedWorkIds: get().activatedWorkIds, entries: nextEntries });
    },
    togglePin: (entryId) => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const nextEntries = get().entries.map(
        (e) => e.id === entryId ? { ...e, pinned: !e.pinned, updatedAt: now } : e
      );
      set({ entries: nextEntries });
      saveNotebookStorage({ activatedWorkIds: get().activatedWorkIds, entries: nextEntries });
    },
    promoteToMemory: (entryId) => {
      const entry = get().entries.find((e) => e.id === entryId);
      if (!entry) return null;
      const hydrated = clientRepository.getItem(entry.workId);
      if (!hydrated) return null;
      const preferredPath = hydrated.accessPaths.find((p) => p.isPreferred) ?? hydrated.accessPaths[0] ?? null;
      const memory = clientRepository.addMemory(entry.workId, {
        type: MemoryType2.PersonalNote,
        title: entry.title,
        body: entry.body,
        privacyState: PrivacyState3.Private,
        spoilerLevel: SpoilerLevel2.Minor,
        showInGlobalGallery: false,
        accessPathId: preferredPath?.id ?? null,
        progressAnchor: hydrated.relationship?.currentAnchor ?? null,
        tags: ["notebook-graduated", entry.category],
        assets: []
      });
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const nextEntries = get().entries.map(
        (e) => e.id === entryId ? { ...e, promotedMemoryId: memory.id, updatedAt: now } : e
      );
      set({ entries: nextEntries });
      saveNotebookStorage({ activatedWorkIds: get().activatedWorkIds, entries: nextEntries });
      return memory;
    }
  };
});
var FANDOM_STORAGE_KEY = "cv_fandom_links_v1";
function createInitialFandomSeed() {
  return [];
}
function loadFandomStorage() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(FANDOM_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
    }
  }
  return createInitialFandomSeed();
}
function saveFandomStorage(links) {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(FANDOM_STORAGE_KEY, JSON.stringify(links));
    } catch {
    }
  }
}
var useFandomLinksStore = create11((set, get) => ({
  links: loadFandomStorage(),
  getLinksForWork: (workId) => {
    return get().links.filter((l) => l.workId === workId).sort((a, b) => a.isPreferred === b.isPreferred ? 0 : a.isPreferred ? -1 : 1);
  },
  addFandomLink: (input) => {
    let parsedUrl;
    try {
      parsedUrl = new URL(input.url.trim());
    } catch {
      throw new Error("Fandom link URL must be a valid HTTP(S) URL.");
    }
    if (parsedUrl.protocol !== "https:" && parsedUrl.protocol !== "http:") {
      throw new Error("Fandom link URL must use HTTP or HTTPS.");
    }
    const existingForWork = get().links.filter((l) => l.workId === input.workId);
    const normalizedUrl = parsedUrl.toString().toLowerCase().replace(/\/+$/, "");
    const existingSameUrl = existingForWork.find(
      (l) => l.url.toLowerCase().replace(/\/+$/, "") === normalizedUrl
    );
    if (existingSameUrl) {
      if (input.isPreferred) {
        get().setPreferredFandomLink(input.workId, existingSameUrl.id);
      }
      return existingSameUrl;
    }
    const shouldBePreferred = input.isPreferred ?? existingForWork.length === 0;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const inferredProvider = input.provider ?? (parsedUrl.hostname.includes("fandom.com") ? "fandom" : parsedUrl.hostname.includes("reddit.com") ? "subreddit" : parsedUrl.hostname.includes("wiki") ? "wiki" : "custom");
    const newLink = {
      id: `fandom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      workId: input.workId,
      communityName: input.communityName.trim() || parsedUrl.hostname,
      provider: inferredProvider,
      canonicalTitle: input.canonicalTitle?.trim() || input.communityName.trim() || parsedUrl.hostname,
      url: parsedUrl.toString(),
      language: input.language?.trim() || "en",
      isPreferred: shouldBePreferred,
      provenance: input.provenance ?? "manual",
      lastCheckedAt: now,
      createdAt: now,
      updatedAt: now
    };
    const updated = get().links.map(
      (l) => shouldBePreferred && l.workId === input.workId ? { ...l, isPreferred: false } : l
    );
    const next = [...updated, newLink];
    set({ links: next });
    saveFandomStorage(next);
    return newLink;
  },
  removeFandomLink: (linkId) => {
    const next = get().links.filter((l) => l.id !== linkId);
    set({ links: next });
    saveFandomStorage(next);
  },
  setPreferredFandomLink: (workId, linkId) => {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const next = get().links.map(
      (l) => l.workId === workId ? { ...l, isPreferred: l.id === linkId, updatedAt: now } : l
    );
    set({ links: next });
    saveFandomStorage(next);
  }
}));
var CURATED_CATALOG_SEEDS = [
  {
    seedId: "cat-gutenberg-frankenstein",
    title: "Frankenstein; or, The Modern Prometheus",
    contentType: "Western Novel",
    coverUrl: "https://www.gutenberg.org/cache/epub/84/pg84.cover.medium.jpg",
    genres: ["Science Fiction", "Gothic", "Horror", "Classic"],
    provider: "gutenberg",
    providerLabel: "Project Gutenberg",
    externalUrl: "https://www.gutenberg.org/ebooks/84",
    accessPathUrl: "https://www.gutenberg.org/ebooks/84.epub3.images",
    accessPathLabel: "Download EPUB (Gutenberg)"
  },
  {
    seedId: "cat-gutenberg-dracula",
    title: "Dracula",
    contentType: "Western Novel",
    coverUrl: "https://www.gutenberg.org/cache/epub/345/pg345.cover.medium.jpg",
    genres: ["Gothic", "Horror", "Supernatural", "Classic"],
    provider: "gutenberg",
    providerLabel: "Project Gutenberg",
    externalUrl: "https://www.gutenberg.org/ebooks/345",
    accessPathUrl: "https://www.gutenberg.org/ebooks/345.epub3.images",
    accessPathLabel: "Download EPUB (Gutenberg)"
  },
  {
    seedId: "cat-gutenberg-timemachine",
    title: "The Time Machine",
    contentType: "Western Novel",
    coverUrl: "https://www.gutenberg.org/cache/epub/35/pg35.cover.medium.jpg",
    genres: ["Science Fiction", "Time Travel", "Adventure", "Classic"],
    provider: "gutenberg",
    providerLabel: "Project Gutenberg",
    externalUrl: "https://www.gutenberg.org/ebooks/35",
    accessPathUrl: "https://www.gutenberg.org/ebooks/35.epub3.images",
    accessPathLabel: "Download EPUB (Gutenberg)"
  },
  {
    seedId: "cat-gutenberg-montecristo",
    title: "The Count of Monte Cristo",
    contentType: "Western Novel",
    coverUrl: "https://www.gutenberg.org/cache/epub/1184/pg1184.cover.medium.jpg",
    genres: ["Adventure", "Revenge", "Historical Fiction", "Drama"],
    provider: "gutenberg",
    providerLabel: "Project Gutenberg",
    externalUrl: "https://www.gutenberg.org/ebooks/1184",
    accessPathUrl: "https://www.gutenberg.org/ebooks/1184.epub3.images",
    accessPathLabel: "Download EPUB (Gutenberg)"
  },
  {
    seedId: "cat-gutenberg-sherlock",
    title: "The Adventures of Sherlock Holmes",
    contentType: "Western Novel",
    coverUrl: "https://www.gutenberg.org/cache/epub/1661/pg1661.cover.medium.jpg",
    genres: ["Mystery", "Detective", "Crime", "Classic"],
    provider: "gutenberg",
    providerLabel: "Project Gutenberg",
    externalUrl: "https://www.gutenberg.org/ebooks/1661",
    accessPathUrl: "https://www.gutenberg.org/ebooks/1661.epub3.images",
    accessPathLabel: "Download EPUB (Gutenberg)"
  },
  {
    seedId: "cat-comick-sololeveling",
    title: "Solo Leveling",
    contentType: "Manhwa",
    coverUrl: "https://meo.comick.pictures/5z6lM.jpg",
    genres: ["Action", "Fantasy", "Manhwa", "Supernatural", "System"],
    provider: "comick",
    providerLabel: "Comick.io",
    externalUrl: "https://comick.io/comic/00-solo-leveling",
    accessPathUrl: "https://comick.io/comic/00-solo-leveling",
    accessPathLabel: "Read Manhwa on Comick.io"
  },
  {
    seedId: "cat-comick-orv",
    title: "Omniscient Reader's Viewpoint",
    contentType: "Manhwa",
    coverUrl: "https://meo.comick.pictures/aO1b8.jpg",
    genres: ["Action", "Apocalyptic", "Fantasy", "Manhwa", "Psychological"],
    provider: "comick",
    providerLabel: "Comick.io",
    externalUrl: "https://comick.io/comic/00-omniscient-reader-s-viewpoint",
    accessPathUrl: "https://comick.io/comic/00-omniscient-reader-s-viewpoint",
    accessPathLabel: "Read Manhwa on Comick.io"
  },
  {
    seedId: "cat-comick-blossomingblade",
    title: "Return of the Blossoming Blade",
    contentType: "Manhwa",
    coverUrl: "https://meo.comick.pictures/yR15q.jpg",
    genres: ["Martial Arts", "Xianxia", "Wuxia", "Action", "Manhwa"],
    provider: "comick",
    providerLabel: "Comick.io",
    externalUrl: "https://comick.io/comic/return-of-the-mount-hua-sect",
    accessPathUrl: "https://comick.io/comic/return-of-the-mount-hua-sect",
    accessPathLabel: "Read on Comick.io"
  },
  {
    seedId: "cat-archive-neuromancer",
    title: "Neuromancer",
    contentType: "Western Novel",
    coverUrl: "https://archive.org/services/img/neuromancer00gibs_0",
    genres: ["Cyberpunk", "Science Fiction", "Dystopia", "AI"],
    provider: "internetarchive",
    providerLabel: "Internet Archive",
    externalUrl: "https://archive.org/details/neuromancer00gibs_0",
    accessPathUrl: "https://archive.org/details/neuromancer00gibs_0",
    accessPathLabel: "Read on Internet Archive Texts"
  },
  {
    seedId: "cat-archive-dune",
    title: "Dune",
    contentType: "Western Novel",
    coverUrl: "https://archive.org/services/img/dune00herb",
    genres: ["Science Fiction", "Space Opera", "Political", "Classic"],
    provider: "internetarchive",
    providerLabel: "Internet Archive",
    externalUrl: "https://archive.org/details/dune00herb",
    accessPathUrl: "https://archive.org/details/dune00herb",
    accessPathLabel: "Borrow & Read on Internet Archive"
  },
  {
    seedId: "cat-goodreads-wayofkings",
    title: "The Way of Kings",
    contentType: "Western Novel",
    coverUrl: "https://covers.openlibrary.org/b/id/7222246-L.jpg",
    genres: ["High Fantasy", "Epic Fantasy", "Progression Fantasy", "Action"],
    provider: "goodreads",
    providerLabel: "Goodreads Top Rated",
    externalUrl: "https://www.goodreads.com/book/show/7235533-the-way-of-kings",
    accessPathUrl: "https://www.goodreads.com/book/show/7235533-the-way-of-kings",
    accessPathLabel: "Goodreads Ratings & Edition Guide"
  },
  {
    seedId: "cat-goodreads-projecthailmary",
    title: "Project Hail Mary",
    contentType: "Western Novel",
    coverUrl: "https://covers.openlibrary.org/b/id/10522967-L.jpg",
    genres: ["Science Fiction", "Space", "Hard Sci-Fi", "Adventure"],
    provider: "goodreads",
    providerLabel: "Goodreads Top Rated",
    externalUrl: "https://www.goodreads.com/book/show/54493401-project-hail-mary",
    accessPathUrl: "https://www.goodreads.com/book/show/54493401-project-hail-mary",
    accessPathLabel: "Goodreads Ratings & Reviews"
  }
];
function generateLocalRecommendations(items, dismissedIds = /* @__PURE__ */ new Set()) {
  const activeItems = items.filter(
    (item) => !item.work.deletedAt && item.work.privacyState !== PrivacyState3.Vault
  );
  const genreWeights = /* @__PURE__ */ new Map();
  const favoriteCreators = /* @__PURE__ */ new Map();
  for (const item of activeItems) {
    const state = item.relationship?.personalState;
    const isEngaged = state === PersonalState6.Reading || state === PersonalState6.CaughtUp || state === PersonalState6.Completed || item.relationship?.shelfTier === "favorite" || item.relationship?.shelfTier === "highlight" || item.relationship?.shelfTier === "priority_watch";
    if (isEngaged) {
      const weight = (item.work.rating ?? 8) >= 9 ? 3 : 2;
      for (const g of item.work.genres) {
        genreWeights.set(g, (genreWeights.get(g) ?? 0) + weight);
      }
      for (const c of item.work.creators ?? []) {
        if (!favoriteCreators.has(c.name.toLowerCase())) {
          favoriteCreators.set(c.name.toLowerCase(), item.work.title);
        }
      }
    }
  }
  const allRelations = clientRepository.getAllWorkRelations();
  const recommendations = [];
  for (const item of activeItems) {
    const workId = item.work.id;
    if (dismissedIds.has(workId)) continue;
    const state = item.relationship?.personalState ?? PersonalState6.Planned;
    const relation = allRelations.find(
      (r) => r.sourceWorkId === workId || r.targetWorkId === workId
    );
    if (relation && (state === PersonalState6.Planned || state === PersonalState6.Paused || state === PersonalState6.Waiting)) {
      const otherId = relation.sourceWorkId === workId ? relation.targetWorkId : relation.sourceWorkId;
      const otherWork = activeItems.find((i) => i.work.id === otherId)?.work;
      if (otherWork) {
        recommendations.push({
          id: `rec-rel-${workId}`,
          workId,
          title: item.work.title,
          contentType: item.work.contentType,
          coverUrl: item.work.coverRef ?? null,
          category: "connected_work",
          reasonLabel: `Connected to "${otherWork.title}" in your Adaptation & Relation Graph`,
          score: 95
        });
        continue;
      }
    }
    const matchingCreator = (item.work.creators ?? []).find((c) => {
      const sourceTitle = favoriteCreators.get(c.name.toLowerCase());
      return sourceTitle && sourceTitle !== item.work.title;
    });
    if (matchingCreator) {
      const sourceTitle = favoriteCreators.get(matchingCreator.name.toLowerCase());
      recommendations.push({
        id: `rec-creator-${workId}`,
        workId,
        title: item.work.title,
        contentType: item.work.contentType,
        coverUrl: item.work.coverRef ?? null,
        category: "same_creator",
        reasonLabel: `By ${matchingCreator.name} (creator of "${sourceTitle}")`,
        score: 90
      });
      continue;
    }
    if (state === PersonalState6.Planned || state === PersonalState6.Paused || state === PersonalState6.Stalled) {
      const matchedGenres = item.work.genres.filter((g) => (genreWeights.get(g) ?? 0) >= 2).sort((a, b) => (genreWeights.get(b) ?? 0) - (genreWeights.get(a) ?? 0));
      if (matchedGenres.length > 0) {
        const topTwo = matchedGenres.slice(0, 2).join(" & ");
        recommendations.push({
          id: `rec-genre-${workId}`,
          workId,
          title: item.work.title,
          contentType: item.work.contentType,
          coverUrl: item.work.coverRef ?? null,
          category: "genre_affinity",
          reasonLabel: state === PersonalState6.Planned ? `On your Plan to Read shelf \xB7 Matches your affinity for ${topTwo}` : `Paused at ${item.relationship?.currentAnchor ? "saved anchor" : "start"} \xB7 Strong match for ${topTwo}`,
          score: 75 + matchedGenres.length * 4
        });
        continue;
      }
    }
    if (state === PersonalState6.Completed || item.relationship?.shelfTier === "highlight" || item.relationship?.shelfTier === "favorite") {
      recommendations.push({
        id: `rec-reread-${workId}`,
        workId,
        title: item.work.title,
        contentType: item.work.contentType,
        coverUrl: item.work.coverRef ?? null,
        category: "reread_highlight",
        reasonLabel: `Completed favorite with ${item.memories.length} saved memor${item.memories.length === 1 ? "y" : "ies"} \xB7 Ideal for a reread pass`,
        score: 65 + item.memories.length * 3
      });
    }
  }
  const existingNormalizedTitles = new Set(
    activeItems.map((i) => i.work.title.toLowerCase().trim())
  );
  for (const seed of CURATED_CATALOG_SEEDS) {
    if (dismissedIds.has(seed.seedId)) continue;
    if (existingNormalizedTitles.has(seed.title.toLowerCase().trim())) continue;
    let matchScore = 0;
    const matchedGenres = [];
    for (const g of seed.genres) {
      const weight = genreWeights.get(g) ?? 0;
      if (weight > 0) {
        matchScore += weight * 6;
        matchedGenres.push(g);
      }
    }
    const baseScore = 65;
    const finalScore = baseScore + matchScore;
    const reason = matchedGenres.length > 0 ? `${seed.providerLabel} \xB7 Matches affinity for ${matchedGenres.slice(0, 2).join(" & ")}` : `${seed.providerLabel} \xB7 Curated catalog work with direct reading access path`;
    recommendations.push({
      id: seed.seedId,
      title: seed.title,
      contentType: seed.contentType,
      coverUrl: seed.coverUrl,
      category: "catalog_discovery",
      reasonLabel: reason,
      score: finalScore,
      provider: seed.provider,
      providerLabel: seed.providerLabel,
      externalUrl: seed.externalUrl,
      accessPathUrl: seed.accessPathUrl,
      accessPathLabel: seed.accessPathLabel,
      genres: seed.genres
    });
  }
  return recommendations.sort((a, b) => b.score - a.score).slice(0, 10);
}
var DISMISSED_RECS_KEY = "cv_dismissed_recommendations_v1";
var useRecommendationsStore = create11((set, get) => ({
  dismissedWorkIds: (() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const raw = window.localStorage.getItem(DISMISSED_RECS_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed;
        }
      } catch {
      }
    }
    return [];
  })(),
  dismissRecommendation: (workId) => {
    const current = get().dismissedWorkIds;
    if (current.includes(workId)) return;
    const next = [...current, workId];
    set({ dismissedWorkIds: next });
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        window.localStorage.setItem(DISMISSED_RECS_KEY, JSON.stringify(next));
      } catch {
      }
    }
  },
  restoreAllDismissed: () => {
    set({ dismissedWorkIds: [] });
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        window.localStorage.removeItem(DISMISSED_RECS_KEY);
      } catch {
      }
    }
  }
}));

// src/stores/use-friends-store.ts
import { create as create12 } from "zustand";
var STORAGE_KEY7 = "cv:friends_and_shared_shelves:v1";
var DEFAULT_FRIENDS = [];
var DEFAULT_ACTIVITY = [];
var DEFAULT_SHARED_SHELVES = [];
var useFriendsStore = create12((set, get) => {
  const loadInitial = () => {
    if (typeof window === "undefined") {
      return {
        friends: [...DEFAULT_FRIENDS],
        activity: [...DEFAULT_ACTIVITY],
        shelves: [...DEFAULT_SHARED_SHELVES]
      };
    }
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY7);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.friends) && Array.isArray(parsed.shelves)) {
          const sanitizedShelves = parsed.shelves.map((shelf) => {
            const isVault = shelf.id === "shelf-dark-vault-guild" || shelf.title.toLowerCase().includes("vault");
            return {
              ...shelf,
              isVaultGated: isVault || shelf.isVaultGated,
              works: shelf.works.map((w) => ({
                ...w,
                recommenderNote: (w.recommenderNote || "").replace(/in C:\\Users\\[^\s.]+/gi, "").trim()
              }))
            };
          });
          return {
            friends: parsed.friends,
            activity: Array.isArray(parsed.activity) ? parsed.activity : [...DEFAULT_ACTIVITY],
            shelves: sanitizedShelves
          };
        }
      }
    } catch {
    }
    return {
      friends: [...DEFAULT_FRIENDS],
      activity: [...DEFAULT_ACTIVITY],
      shelves: [...DEFAULT_SHARED_SHELVES]
    };
  };
  const initial = loadInitial();
  const persist = (friends, shelves, activity) => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          STORAGE_KEY7,
          JSON.stringify({ friends, shelves, activity })
        );
      }
    } catch {
    }
  };
  return {
    friends: initial.friends,
    activityFeed: initial.activity,
    sharedShelves: initial.shelves,
    toggleFollow: (friendId) => {
      set((state) => {
        const nextFriends = state.friends.map(
          (f) => f.id === friendId ? { ...f, isFollowing: !f.isFollowing } : f
        );
        persist(nextFriends, state.sharedShelves, state.activityFeed);
        return { friends: nextFriends };
      });
    },
    removeFriend: (friendId) => {
      set((state) => {
        const nextFriends = state.friends.filter((f) => f.id !== friendId);
        persist(nextFriends, state.sharedShelves, state.activityFeed);
        return { friends: nextFriends };
      });
    },
    addWorkToSharedShelf: (shelfId, work) => {
      set((state) => {
        const nextShelves = state.sharedShelves.map((shelf) => {
          if (shelf.id !== shelfId) return shelf;
          const newEntry = {
            id: `entry-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            workId: work.id,
            title: work.title,
            contentType: work.contentType,
            coverRef: work.coverRef,
            addedByHandle: "@Archivist",
            recommenderNote: work.note?.trim() || "Recommended by Archivist",
            upvotesCount: 1,
            addedAt: (/* @__PURE__ */ new Date()).toISOString()
          };
          return {
            ...shelf,
            works: [newEntry, ...shelf.works],
            updatedAt: (/* @__PURE__ */ new Date()).toISOString()
          };
        });
        persist(state.friends, nextShelves, state.activityFeed);
        return { sharedShelves: nextShelves };
      });
    },
    removeWorkFromSharedShelf: (shelfId, entryId) => {
      set((state) => {
        const nextShelves = state.sharedShelves.map((shelf) => {
          if (shelf.id !== shelfId) return shelf;
          return {
            ...shelf,
            works: shelf.works.filter((w) => w.id !== entryId),
            updatedAt: (/* @__PURE__ */ new Date()).toISOString()
          };
        });
        persist(state.friends, nextShelves, state.activityFeed);
        return { sharedShelves: nextShelves };
      });
    },
    deleteSharedShelf: (shelfId) => {
      set((state) => {
        const nextShelves = state.sharedShelves.filter((shelf) => shelf.id !== shelfId);
        persist(state.friends, nextShelves, state.activityFeed);
        return { sharedShelves: nextShelves };
      });
    },
    flushActivityFeed: () => {
      set((state) => {
        persist(state.friends, state.sharedShelves, []);
        return { activityFeed: [] };
      });
    },
    upvoteShelfWork: (shelfId, entryId) => {
      set((state) => {
        const nextShelves = state.sharedShelves.map((shelf) => {
          if (shelf.id !== shelfId) return shelf;
          return {
            ...shelf,
            works: shelf.works.map(
              (w) => w.id === entryId ? { ...w, upvotesCount: w.upvotesCount + 1 } : w
            )
          };
        });
        persist(state.friends, nextShelves, state.activityFeed);
        return { sharedShelves: nextShelves };
      });
    },
    createSharedShelf: (title, description, iconEmoji = "\u{1F4DA}", isPublic = true) => {
      const newShelf = {
        id: `shelf-${Date.now()}`,
        title: title.trim(),
        description: description.trim(),
        iconEmoji,
        createdByHandle: "@Archivist",
        memberHandles: ["@Archivist"],
        isPublicList: isPublic,
        publicShareCode: `${title.slice(0, 4).toUpperCase()}-${Date.now().toString().slice(-4)}`,
        updatedAt: (/* @__PURE__ */ new Date()).toISOString(),
        works: []
      };
      set((state) => {
        const nextShelves = [newShelf, ...state.sharedShelves];
        persist(state.friends, nextShelves, state.activityFeed);
        return { sharedShelves: nextShelves };
      });
      return newShelf;
    }
  };
});

// src/stores/use-fan-creations-store.ts
import { create as create13 } from "zustand";
import {
  ContentType as ContentType4,
  PersonalState as PersonalState7,
  WorkRelationType as WorkRelationType3,
  ContentOrigin as ContentOrigin3,
  PublicationState as PublicationState3,
  PrivacyState as PrivacyState4
} from "@grimoire/domain";
var STORAGE_KEY8 = "cv:fan_creations_store:v1";
var DEFAULT_FANFICTIONS = [];
var DEFAULT_VIDEO_EDITS = [];
var DEFAULT_FAN_ART = [];
var useFanCreationsStore = create13((set, get) => {
  const loadInitial = () => {
    if (typeof window === "undefined") {
      return {
        fanfics: [...DEFAULT_FANFICTIONS],
        edits: [...DEFAULT_VIDEO_EDITS],
        art: [...DEFAULT_FAN_ART]
      };
    }
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY8);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.fanfics) && Array.isArray(parsed.edits)) {
          return parsed;
        }
      }
    } catch {
    }
    return {
      fanfics: [...DEFAULT_FANFICTIONS],
      edits: [...DEFAULT_VIDEO_EDITS],
      art: [...DEFAULT_FAN_ART]
    };
  };
  const initial = loadInitial();
  const persist = (fanfics, edits, art) => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          STORAGE_KEY8,
          JSON.stringify({ fanfics, edits, art })
        );
      }
    } catch {
    }
  };
  return {
    fanfictions: initial.fanfics,
    videoEdits: initial.edits,
    fanArt: initial.art,
    getFanfictionsForWork: (workId) => {
      return get().fanfictions.filter((f) => f.workId === workId);
    },
    getVideoEditsForWork: (workId) => {
      return get().videoEdits.filter((e) => e.workId === workId);
    },
    getFanArtForWork: (workId) => {
      return get().fanArt.filter((a) => a.workId === workId);
    },
    addFanfiction: (entry) => {
      const created = {
        ...entry,
        id: `fanfic-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        isPromotedToWork: false,
        createdAt: (/* @__PURE__ */ new Date()).toISOString()
      };
      set((state) => {
        const next = [created, ...state.fanfictions];
        persist(next, state.videoEdits, state.fanArt);
        return { fanfictions: next };
      });
      return created;
    },
    promoteFanfictionToWork: (fanficId) => {
      const fanfic = get().fanfictions.find((f) => f.id === fanficId);
      if (!fanfic) return null;
      const parentItem = clientRepository.getItems().find((i) => i.work.id === fanfic.workId);
      const parentTitle = parentItem?.work.title ?? "Original Work";
      const createdItem = clientRepository.createWork(
        {
          title: `[Fanfic] ${fanfic.title}`,
          alternativeTitles: [`${fanfic.title} (${fanfic.author})`],
          aliases: [fanfic.title],
          contentType: ContentType4.Fanfiction,
          origin: ContentOrigin3.Western,
          publicationState: PublicationState3.Completed,
          privacyState: PrivacyState4.Public,
          coverRef: parentItem?.work.coverRef ?? null,
          externalIds: [],
          description: `Promoted from Fanfiction section of "${parentTitle}". Original Author: ${fanfic.author} (${fanfic.platform}).

Summary:
${fanfic.summary}`,
          genres: ["Fanfiction", ...fanfic.tags],
          tags: ["promoted-fanfic", fanfic.platform.toLowerCase(), fanfic.rating.toLowerCase()],
          creators: [
            {
              id: `creator-${Date.now()}`,
              workId: "",
              name: fanfic.author,
              role: "author",
              sortOrder: 0
            }
          ],
          totalChapters: fanfic.chaptersCount
        },
        PersonalState7.Reading,
        { type: "chapter", chapter: 1, title: "Chapter 1" },
        fanfic.sourceUrl ? {
          label: `${fanfic.platform} Story Page`,
          url: fanfic.sourceUrl,
          provider: fanfic.platform
        } : void 0
      );
      clientRepository.linkWorkRelation(
        createdItem.work.id,
        fanfic.workId,
        WorkRelationType3.FanfictionOf,
        `Promoted fanfiction of ${parentTitle} by ${fanfic.author}`
      );
      set((state) => {
        const next = state.fanfictions.map(
          (f) => f.id === fanficId ? { ...f, isPromotedToWork: true, promotedWorkId: createdItem.work.id } : f
        );
        persist(next, state.videoEdits, state.fanArt);
        return { fanfictions: next };
      });
      return createdItem.work;
    },
    addVideoEdit: (entry) => {
      const created = {
        ...entry,
        id: `edit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        createdAt: (/* @__PURE__ */ new Date()).toISOString()
      };
      set((state) => {
        const next = [created, ...state.videoEdits];
        persist(state.fanfictions, next, state.fanArt);
        return { videoEdits: next };
      });
      return created;
    },
    addFanArt: (entry) => {
      const created = {
        ...entry,
        id: `art-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        createdAt: (/* @__PURE__ */ new Date()).toISOString()
      };
      set((state) => {
        const next = [created, ...state.fanArt];
        persist(state.fanfictions, state.videoEdits, next);
        return { fanArt: next };
      });
      return created;
    },
    removeFanfiction: (id) => {
      set((state) => {
        const next = state.fanfictions.filter((f) => f.id !== id);
        persist(next, state.videoEdits, state.fanArt);
        return { fanfictions: next };
      });
    },
    removeVideoEdit: (id) => {
      set((state) => {
        const next = state.videoEdits.filter((e) => e.id !== id);
        persist(state.fanfictions, next, state.fanArt);
        return { videoEdits: next };
      });
    },
    removeFanArt: (id) => {
      set((state) => {
        const next = state.fanArt.filter((a) => a.id !== id);
        persist(state.fanfictions, state.videoEdits, next);
        return { fanArt: next };
      });
    }
  };
});

// src/global-search.ts
import { MemoryType as MemoryType3, PrivacyState as PrivacyState5, SpoilerLevel as SpoilerLevel3 } from "@grimoire/domain";
var SEARCH_CATEGORY_LABELS = {
  works: "Works",
  memories: "Memories",
  lore: "Lore",
  sources: "Sources",
  files: "Local files",
  vault: "Private Vault"
};
var SEARCH_CATEGORY_ORDER = [
  "works",
  "memories",
  "lore",
  "sources",
  "files",
  "vault"
];
var MAX_TOKENS = 8;
var MAX_TOKEN_LENGTH = 64;
var SNIPPET_RADIUS_BEFORE = 40;
var SNIPPET_LENGTH = 120;
function tokenizeQuery(query) {
  return query.toLowerCase().split(/\s+/).map((token) => token.trim().slice(0, MAX_TOKEN_LENGTH)).filter((token) => token.length > 0).slice(0, MAX_TOKENS);
}
function scoreFields(tokens, fields) {
  let total = 0;
  for (const token of tokens) {
    let best = 0;
    for (const field of fields) {
      if (!field.text) continue;
      if (field.text.toLowerCase().includes(token) && field.weight > best) {
        best = field.weight;
      }
    }
    if (best === 0) return null;
    total += best;
  }
  return { score: total };
}
function makeSnippet(text, tokens) {
  if (!text) return null;
  const lower = text.toLowerCase();
  let index = -1;
  for (const token of tokens) {
    const found = lower.indexOf(token);
    if (found !== -1 && (index === -1 || found < index)) index = found;
  }
  const start = Math.max(0, index === -1 ? 0 : index - SNIPPET_RADIUS_BEFORE);
  const slice = text.slice(start, start + SNIPPET_LENGTH).replace(/\s+/g, " ").trim();
  const prefix = start > 0 ? "\u2026" : "";
  const suffix = start + SNIPPET_LENGTH < text.length ? "\u2026" : "";
  return `${prefix}${slice}${suffix}`;
}
function safeHost(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return parsed.host;
  } catch {
    return null;
  }
}
function sortAndLimit(results, limit) {
  return results.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title)).slice(0, limit);
}
function runGlobalSearch(input) {
  const tokens = tokenizeQuery(input.query);
  if (tokens.length === 0) return [];
  const limit = input.limitPerCategory ?? 8;
  const byCategory = {
    works: [],
    memories: [],
    lore: [],
    sources: [],
    files: [],
    vault: []
  };
  const workTitles = /* @__PURE__ */ new Map();
  for (const work of input.works) {
    workTitles.set(work.id, work.title);
  }
  for (const work of input.works) {
    if (work.privacyState === PrivacyState5.Vault) continue;
    if (work.deletedAt || work.mergedIntoWorkId) continue;
    const match = scoreFields(tokens, [
      { text: work.title, weight: 3 },
      { text: work.aliases.join(" "), weight: 2 },
      { text: work.genres.join(" "), weight: 1 },
      { text: work.tags.join(" "), weight: 1 },
      { text: work.description, weight: 1 }
    ]);
    if (match) {
      byCategory.works.push({
        key: `works:${work.id}`,
        category: "works",
        title: work.title,
        subtitle: work.genres.length > 0 ? work.genres.slice(0, 3).join(" \xB7 ") : null,
        snippet: null,
        route: `/work/${encodeURIComponent(work.id)}`,
        score: match.score
      });
    }
    for (const path of work.accessPaths) {
      const host = safeHost(path.url ?? null);
      const label = path.label ?? host ?? path.kind;
      const pathMatch = scoreFields(tokens, [
        { text: label, weight: 2 },
        { text: host, weight: 1 }
      ]);
      if (!pathMatch) continue;
      byCategory.sources.push({
        key: `sources:${path.id}`,
        category: "sources",
        title: label,
        subtitle: work.title,
        snippet: host,
        route: `/work/${encodeURIComponent(work.id)}`,
        score: pathMatch.score
      });
    }
  }
  for (const memory of input.memories) {
    if (memory.privacyState === PrivacyState5.Vault) continue;
    if (memory.spoilerLevel === SpoilerLevel3.Major) continue;
    const isLore = memory.type === MemoryType3.LoreEntry;
    const hideBody = memory.spoilerLevel === SpoilerLevel3.Minor;
    const match = scoreFields(tokens, [
      { text: memory.title, weight: 3 },
      { text: memory.tags.join(" "), weight: 2 },
      { text: memory.readingAttemptId, weight: 1 },
      { text: hideBody ? null : memory.body, weight: 1 }
    ]);
    if (!match) continue;
    const workTitle = memory.workId ? workTitles.get(memory.workId) ?? null : null;
    const subtitleParts = [
      workTitle,
      memory.readingAttemptId ? `Pass ${memory.readingAttemptId}` : null
    ].filter((part) => Boolean(part));
    const snippet = hideBody ? null : makeSnippet(memory.body, tokens);
    const route = memory.workId ? `/work/${encodeURIComponent(memory.workId)}` : "/memory";
    byCategory[isLore ? "lore" : "memories"].push({
      key: `${isLore ? "lore" : "memories"}:${memory.id}`,
      category: isLore ? "lore" : "memories",
      title: memory.title?.trim() || (isLore ? "Lore entry" : "Untitled memory"),
      subtitle: subtitleParts.length > 0 ? subtitleParts.join(" \xB7 ") : null,
      snippet: hideBody ? "Spoiler-protected. Reveal it in Memory to read." : snippet,
      route,
      score: match.score
    });
  }
  for (const file of input.files) {
    const workTitle = file.workId ? workTitles.get(file.workId) ?? null : null;
    const match = scoreFields(tokens, [
      { text: file.displayName, weight: 3 },
      { text: file.format, weight: 1 }
    ]);
    if (!match) continue;
    byCategory.files.push({
      key: `files:${file.id}`,
      category: "files",
      title: file.displayName,
      subtitle: workTitle ?? "Unmatched file",
      snippet: null,
      route: `/reader/${encodeURIComponent(file.id)}`,
      score: match.score
    });
  }
  if (input.vaultUnlocked && input.vaultNotes) {
    for (const note of input.vaultNotes) {
      const match = scoreFields(tokens, [
        { text: note.title, weight: 3 },
        { text: note.body, weight: 1 }
      ]);
      if (!match) continue;
      byCategory.vault.push({
        key: `vault:${note.id}`,
        category: "vault",
        title: note.title || "Untitled note",
        subtitle: null,
        snippet: makeSnippet(note.body, tokens),
        route: "/vault",
        score: match.score
      });
    }
  }
  const groups = [];
  for (const category of SEARCH_CATEGORY_ORDER) {
    const results = sortAndLimit(byCategory[category], limit);
    if (results.length > 0) {
      groups.push({ category, label: SEARCH_CATEGORY_LABELS[category], results });
    }
  }
  return groups;
}

// src/publication/publication-engine.ts
import { PrivacyState as PrivacyState6 } from "@grimoire/domain";
var SHINGLE_WORDS = 8;
var MIN_WHOLE_NEEDLE_LENGTH = 12;
var PRIVATE_TAG_NAMES = /* @__PURE__ */ new Set(["private", "vault", "secret", "confidential", "hidden", "personal"]);
var PRIVATE_TAG_PREFIXES = ["private:", "vault:", "secret:", "_private:"];
var MARKER_PATTERN = /\[(private|vault)\]|\[\/(private|vault)\]|\|\|(private|vault):/i;
var VAULT_REFERENCE_PATTERN = /\bprivate vault\b|\bvault:[\w-]+|vault:\/\//i;
function normalizeForScan(text) {
  return text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").replace(/\s+/g, " ").trim();
}
function isPrivateTagName(tag) {
  const clean = tag.trim().replace(/^#/, "").toLowerCase();
  if (PRIVATE_TAG_NAMES.has(clean)) return true;
  return PRIVATE_TAG_PREFIXES.some((prefix) => clean.startsWith(prefix));
}
function privateNeedles(text) {
  const normalized = normalizeForScan(text);
  if (!normalized) return [];
  const words = normalized.split(" ");
  if (words.length < SHINGLE_WORDS) {
    return normalized.length >= MIN_WHOLE_NEEDLE_LENGTH ? [normalized] : [];
  }
  const needles = [];
  for (let i = 0; i + SHINGLE_WORDS <= words.length; i++) {
    needles.push(words.slice(i, i + SHINGLE_WORDS).join(" "));
  }
  return needles;
}
var CONTENT_TYPE_LABELS = {
  manga: "Manga",
  manhwa: "Manhwa",
  manhua: "Manhua",
  webtoon: "Webtoon",
  light_novel: "Light novel",
  web_novel: "Web novel",
  western_novel: "Novel",
  anime: "Anime",
  donghua: "Donghua",
  epub: "EPUB",
  pdf: "PDF",
  cbz: "CBZ"
};
function contentTypeLabel(value) {
  if (!value) return "Work";
  const known = CONTENT_TYPE_LABELS[value];
  if (known) return known;
  const spaced = value.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
function safeHttpsUrl(value) {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}
function scanText(ctx, text, entryId, field) {
  if (!text) return;
  if (MARKER_PATTERN.test(text)) {
    ctx.findings.push({
      kind: "private_marker",
      entryId,
      field,
      message: `${field} contains an inline private marker.`
    });
  }
  if (VAULT_REFERENCE_PATTERN.test(text)) {
    ctx.findings.push({
      kind: "vault_reference",
      entryId,
      field,
      message: `${field} refers to the Private Vault.`
    });
  }
  const normalized = normalizeForScan(text);
  if (normalized && ctx.needles.some((needle) => normalized.includes(needle))) {
    ctx.findings.push({
      kind: "private_text_match",
      entryId,
      field,
      message: `${field} repeats text from a private memory or vault note.`
    });
  }
  if (normalized && ctx.rereadNeedles.some((needle) => normalized.includes(needle))) {
    ctx.findings.push({
      kind: "reread_attempt_leak",
      entryId,
      field,
      message: `${field} repeats text or attempt identifier from a private reread memory.`
    });
  }
}
function scanTags(ctx, tags, entryId, field) {
  if (tags.some(isPrivateTagName)) {
    ctx.findings.push({
      kind: "private_tag",
      entryId,
      field,
      message: `${field} includes a private tag.`
    });
  }
}
function evaluatePublication(input) {
  const { list, works, privateSources, vaultStatus, generatedAt } = input;
  const excluded = new Set(input.excludedEntryIds ?? []);
  const worksById = new Map(works.map((work) => [work.id, work]));
  const needles = privateSources.flatMap((source) => privateNeedles(source.text));
  const rereadNeedles = privateSources.filter((source) => Boolean(source.readingAttemptId)).flatMap((source) => [
    ...privateNeedles(source.text),
    ...source.readingAttemptId ? [normalizeForScan(source.readingAttemptId)] : []
  ]).filter(Boolean);
  const ctx = {
    needles: [...new Set(needles)],
    rereadNeedles: [...new Set(rereadNeedles)],
    findings: []
  };
  if (list.privacyState !== PrivacyState6.Public) {
    ctx.findings.push({
      kind: "list_not_public",
      entryId: null,
      field: "list",
      message: "The list is not public. Set it to Public before publishing."
    });
  }
  if (vaultStatus === "locked") {
    ctx.findings.push({
      kind: "vault_unverified",
      entryId: null,
      field: "vault",
      message: "Unlock the Private Vault so every private note can be checked before publishing."
    });
  }
  scanText(ctx, list.title, null, "list title");
  scanText(ctx, list.description, null, "list description");
  const orderedItems = [...list.items].sort((a, b) => a.position - b.position);
  const entries = [];
  for (const item of orderedItems) {
    if (excluded.has(item.id)) continue;
    const work = worksById.get(item.workId);
    if (!work) {
      ctx.findings.push({
        kind: "work_missing",
        entryId: item.id,
        field: "work",
        message: "This entry refers to a work that no longer exists."
      });
      continue;
    }
    if (work.privacyState === PrivacyState6.Private || work.privacyState === PrivacyState6.Vault) {
      ctx.findings.push({
        kind: "work_private",
        entryId: item.id,
        field: "work",
        message: "This work is private or in the vault. Exclude it from the publication."
      });
    }
    if (item.readingAttemptId) {
      ctx.findings.push({
        kind: "reread_attempt_leak",
        entryId: item.id,
        field: "personal note",
        message: "This note is tied to an earlier reread pass and must be cleared or excluded before publishing."
      });
    }
    scanTags(ctx, work.tags, item.id, "work tags");
    scanTags(ctx, work.genres, item.id, "work genres");
    scanText(ctx, work.title, item.id, "work title");
    scanText(ctx, work.author, item.id, "author");
    scanText(ctx, work.synopsis, item.id, "synopsis");
    scanText(ctx, item.personalNote, item.id, "personal note");
    entries.push({
      entryId: item.id,
      position: entries.length,
      title: work.title,
      author: work.author,
      contentType: contentTypeLabel(work.contentType),
      genres: [...work.genres],
      synopsis: work.synopsis,
      personalNote: item.personalNote,
      rating: work.rating,
      totalChapters: work.totalChapters,
      coverUrl: safeHttpsUrl(work.coverRef)
    });
  }
  if (ctx.findings.length > 0) {
    return { status: "blocked", document: null, findings: ctx.findings };
  }
  return {
    status: "ready",
    findings: [],
    document: {
      title: list.title,
      description: list.description,
      generatedAt,
      entries
    }
  };
}
function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function escapeMarkdown(value) {
  return value.replace(/([\\`*_{}\[\]()#+\-.!|<>])/g, "\\$1");
}
function formatRating(rating) {
  return rating === null ? null : `${rating.toFixed(1)} / 10`;
}
function formatRatingScaleMarkdown(rating) {
  if (rating === null) return null;
  const clamped = Math.max(0, Math.min(10, rating));
  const filled = Math.round(clamped);
  const bar = "\u25A0".repeat(filled) + "\u25A1".repeat(10 - filled);
  return `[${bar}] ${clamped.toFixed(1)}/10`;
}
function renderPublicationMarkdown(doc) {
  const lines = [];
  lines.push(`# ${escapeMarkdown(doc.title)}`);
  lines.push("");
  lines.push(`\`CURATED EDITION \xB7 ${doc.entries.length} WORKS \xB7 ${escapeMarkdown(doc.generatedAt)}\``);
  lines.push("");
  if (doc.description) {
    lines.push(`> ${escapeMarkdown(doc.description)}`);
    lines.push("");
  }
  doc.entries.forEach((entry, index) => {
    const idxLabel = String(index + 1).padStart(2, "0");
    const meta = [entry.author, entry.contentType, entry.totalChapters, formatRatingScaleMarkdown(entry.rating)].filter((part) => Boolean(part)).map(escapeMarkdown).join(" \xB7 ");
    lines.push(`${index + 1}. \`[${idxLabel}]\` **${escapeMarkdown(entry.title)}**${meta ? ` \u2014 ${meta}` : ""}`);
    if (entry.genres.length > 0) {
      lines.push(`   Genres: ${entry.genres.map(escapeMarkdown).join(" / ")}`);
    }
    if (entry.synopsis) {
      lines.push(`   ${escapeMarkdown(entry.synopsis)}`);
    }
    if (entry.personalNote) {
      lines.push(`   _${escapeMarkdown(entry.personalNote)}_`);
    }
    lines.push("");
  });
  lines.push(`_Generated ${escapeMarkdown(doc.generatedAt)} \xB7 Grimoire_`);
  return lines.join("\n") + "\n";
}
var EDITORIAL_CSS = `
:root {
  color-scheme: light dark;
  --ink: #0f0e13;
  --muted: #5b5768;
  --subtle: #8b8698;
  --rule: #e2dfe8;
  --surface: #f2f0f7;
  --accent: #ec4899;
  --accent-telemetry: #7c3aed;
  --paper: #faf9fc;
}
@media (prefers-color-scheme: dark) {
  :root {
    --ink: #f6f4fa;
    --muted: #a69fb8;
    --subtle: #6e6782;
    --rule: #252031;
    --surface: #14111d;
    --accent: #f472b6;
    --accent-telemetry: #d2ff00;
    --paper: #09080d;
  }
}
* { box-sizing: border-box; }
body {
  margin: 0;
  background: var(--paper);
  color: var(--ink);
  font: 16px/1.65 "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  -webkit-font-smoothing: antialiased;
}
main {
  max-width: 56rem;
  margin: 0 auto;
  padding: 3.5rem 1.5rem 6rem;
}
header {
  border-top: 2px solid var(--ink);
  border-bottom: 1px solid var(--rule);
  padding: 2rem 0 2.25rem;
  margin-bottom: 2.5rem;
  position: relative;
}
.eyebrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font: 600 0.72rem/1 "JetBrains Mono", ui-monospace, monospace;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0 0 1rem;
}
h1 {
  font-size: clamp(2.25rem, 5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.05;
  margin: 0 0 0.85rem;
  letter-spacing: -0.03em;
}
.standfirst {
  color: var(--muted);
  font-size: 1.125rem;
  line-height: 1.55;
  max-width: 42rem;
  margin: 0;
}
ol {
  list-style: none;
  counter-reset: entry;
  padding: 0;
  margin: 0;
}
li {
  counter-increment: entry;
  display: grid;
  grid-template-columns: 4.5rem 1fr auto;
  gap: 1.5rem;
  align-items: start;
  padding: 2rem 0;
  border-bottom: 1px solid var(--rule);
}
@media (max-width: 640px) {
  li { grid-template-columns: 3rem 1fr; }
  .cover { grid-column: 2; }
}
.index-col {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding-top: 0.15rem;
}
.index-num {
  font: 800 1.65rem/1 "JetBrains Mono", ui-monospace, monospace;
  letter-spacing: -0.04em;
  color: var(--ink);
}
.index-bar {
  width: 2rem;
  height: 3px;
  background: var(--accent);
}
.body-col { min-width: 0; }
.title {
  font-size: 1.45rem;
  font-weight: 750;
  letter-spacing: -0.02em;
  line-height: 1.25;
  margin: 0 0 0.35rem;
}
.meta {
  font: 500 0.8rem/1.4 "JetBrains Mono", ui-monospace, monospace;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
  margin: 0 0 0.75rem;
}
.scale {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  font: 600 0.75rem/1 "JetBrains Mono", ui-monospace, monospace;
  color: var(--ink);
  background: var(--surface);
  border: 1px solid var(--rule);
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  margin: 0 0 0.85rem;
}
.scale-bar {
  width: 7rem;
  height: 0.4rem;
  border-radius: 2px;
  background: var(--rule);
  overflow: hidden;
}
.scale-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--accent), var(--accent-telemetry));
}
.genres {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0 0 0.8rem;
}
.genre-pill {
  font: 500 0.72rem/1 "JetBrains Mono", ui-monospace, monospace;
  color: var(--muted);
  background: var(--surface);
  border: 1px solid var(--rule);
  border-radius: 999px;
  padding: 0.25rem 0.6rem;
}
.synopsis {
  color: var(--muted);
  font-size: 0.95rem;
  margin: 0 0 0.75rem;
}
.note {
  border-left: 3px solid var(--accent);
  background: var(--surface);
  padding: 0.65rem 0.95rem;
  border-radius: 0 6px 6px 0;
  font-size: 0.95rem;
  margin: 0.5rem 0 0;
}
.cover {
  width: 5.5rem;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--rule);
}
footer {
  margin-top: 3rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--rule);
  display: flex;
  justify-content: space-between;
  font: 500 0.75rem/1.4 "JetBrains Mono", ui-monospace, monospace;
  color: var(--subtle);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
`;
function renderPublicationHtml(doc) {
  const items = doc.entries.map((entry, idx) => {
    const idxLabel = String(idx + 1).padStart(2, "0");
    const meta = [entry.author, entry.contentType, entry.totalChapters].filter((part) => Boolean(part)).map(escapeHtml).join(" \xB7 ");
    const scale = entry.rating === null ? "" : `<div class="scale"><span class="scale-bar"><span class="scale-fill" style="width:${Math.max(
      0,
      Math.min(100, entry.rating * 10)
    ).toFixed(0)}%"></span></span><span>${escapeHtml(formatRating(entry.rating) ?? "")}</span></div>`;
    const cover = entry.coverUrl ? `<img class="cover" src="${escapeHtml(entry.coverUrl)}" alt="" loading="lazy" referrerpolicy="no-referrer">` : "";
    const genres = entry.genres.length > 0 ? `<div class="genres">${entry.genres.map((g) => `<span class="genre-pill">${escapeHtml(g)}</span>`).join("")}</div>` : "";
    const synopsis = entry.synopsis ? `<p class="synopsis">${escapeHtml(entry.synopsis)}</p>` : "";
    const note = entry.personalNote ? `<blockquote class="note">${escapeHtml(entry.personalNote)}</blockquote>` : "";
    return `<li><div class="index-col"><span class="index-num">${idxLabel}</span><span class="index-bar"></span></div><div class="body-col"><h2 class="title">${escapeHtml(entry.title)}</h2><p class="meta">${meta}</p>${scale}${genres}${synopsis}${note}</div>${cover}</li>`;
  }).join("\n");
  const description = doc.description ? `<p class="standfirst">${escapeHtml(doc.description)}</p>` : "";
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src https:">
<meta name="referrer" content="no-referrer">
<title>${escapeHtml(doc.title)}</title>
<style>${EDITORIAL_CSS}</style>
</head>
<body>
<main>
<header>
<p class="eyebrow"><span>Grimoire // Curated Index</span><span>${doc.entries.length} Works</span></p>
<h1>${escapeHtml(doc.title)}</h1>
${description}
</header>
<ol>
${items}
</ol>
<footer><span>Generated ${escapeHtml(doc.generatedAt)}</span><span>Grimoire</span></footer>
</main>
</body>
</html>
`;
}

// src/transfer/zip-store.ts
var LOCAL_FILE_HEADER = 67324752;
var CENTRAL_DIRECTORY_HEADER = 33639248;
var END_OF_CENTRAL_DIRECTORY = 101010256;
var MAX_ENTRIES = 65535;
var MAX_ENTRY_NAME_LENGTH = 255;
var UTF8_FLAG = 2048;
var CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 3988292384 ^ c >>> 1 : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();
function crc32(data) {
  let crc = 4294967295;
  for (let i = 0; i < data.length; i++) {
    crc = (CRC_TABLE[(crc ^ data[i]) & 255] ?? 0) ^ crc >>> 8;
  }
  return (crc ^ 4294967295) >>> 0;
}
function validateZipEntryName(name) {
  if (!name || name.length > MAX_ENTRY_NAME_LENGTH) return "Entry name is empty or too long.";
  if (name.startsWith("/") || /^[a-zA-Z]:/.test(name)) return "Absolute paths are not allowed.";
  if (name.includes("\\")) return "Backslashes are not allowed in entry names.";
  if (name.split("/").some((part) => part === "..")) return "Path traversal is not allowed.";
  if (/[\u0000-\u001f]/.test(name)) return "Control characters are not allowed.";
  return null;
}
function createStoredZip(entries) {
  if (entries.length === 0) throw new Error("A ZIP archive needs at least one entry.");
  if (entries.length > MAX_ENTRIES) throw new Error("Too many entries for a single ZIP archive.");
  const encoder = new TextEncoder();
  const seen = /* @__PURE__ */ new Set();
  const locals = [];
  const centrals = [];
  let offset = 0;
  for (const entry of entries) {
    const problem = validateZipEntryName(entry.name);
    if (problem) throw new Error(`${entry.name}: ${problem}`);
    if (seen.has(entry.name)) throw new Error(`Duplicate entry name: ${entry.name}`);
    seen.add(entry.name);
    const nameBytes = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.length;
    const local = new Uint8Array(30 + nameBytes.length + size);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, LOCAL_FILE_HEADER, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, UTF8_FLAG, true);
    lv.setUint16(8, 0, true);
    lv.setUint16(10, 0, true);
    lv.setUint16(12, 33, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);
    lv.setUint32(22, size, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    local.set(nameBytes, 30);
    local.set(entry.data, 30 + nameBytes.length);
    locals.push(local);
    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, CENTRAL_DIRECTORY_HEADER, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, UTF8_FLAG, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 33, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, offset, true);
    central.set(nameBytes, 46);
    centrals.push(central);
    offset += local.length;
  }
  const centralSize = centrals.reduce((sum, part) => sum + part.length, 0);
  if (offset > 4294967295 || centralSize > 4294967295) {
    throw new Error("Archive is too large for the ZIP stored format.");
  }
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, END_OF_CENTRAL_DIRECTORY, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  ev.setUint16(20, 0, true);
  const out = new Uint8Array(offset + centralSize + end.length);
  let cursor = 0;
  for (const part of locals) {
    out.set(part, cursor);
    cursor += part.length;
  }
  for (const part of centrals) {
    out.set(part, cursor);
    cursor += part.length;
  }
  out.set(end, cursor);
  return out;
}
function readStoredZip(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let eocd = -1;
  for (let i = bytes.length - 22; i >= 0 && i >= bytes.length - 22 - 65535; i--) {
    if (view.getUint32(i, true) === END_OF_CENTRAL_DIRECTORY) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("Not a ZIP archive.");
  const count = view.getUint16(eocd + 10, true);
  let cursor = view.getUint32(eocd + 16, true);
  const results = [];
  for (let n = 0; n < count; n++) {
    if (view.getUint32(cursor, true) !== CENTRAL_DIRECTORY_HEADER) throw new Error("Corrupt central directory.");
    const method = view.getUint16(cursor + 10, true);
    const crc = view.getUint32(cursor + 16, true);
    const size = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localOffset = view.getUint32(cursor + 42, true);
    const name = decoder.decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
    cursor += 46 + nameLength + extraLength + commentLength;
    if (method !== 0) throw new Error(`${name}: compressed entries are not supported.`);
    const problem = validateZipEntryName(name);
    if (problem) throw new Error(`${name}: ${problem}`);
    if (view.getUint32(localOffset, true) !== LOCAL_FILE_HEADER) throw new Error(`${name}: corrupt local header.`);
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLength + localExtraLength;
    const data = bytes.slice(dataStart, dataStart + size);
    if (data.length !== size) throw new Error(`${name}: truncated entry.`);
    if (crc32(data) !== crc) throw new Error(`${name}: CRC-32 mismatch.`);
    results.push({ name, data });
  }
  return results;
}

// src/transfer/transfer.ts
var BACKUP_FORMAT = "grimoire-backup";
var BACKUP_VERSION = 1;
var VAULT_EXPORT_CONFIRMATION = "INCLUDE VAULT";
var MAX_IMPORT_CHARACTERS = 25e6;
var MAX_IMPORT_ROWS = 1e4;
function buildBackup(source, options) {
  const includeVault = options.includeVault === true;
  if (includeVault && options.vaultConfirmation !== VAULT_EXPORT_CONFIRMATION) {
    throw new Error(`Vault export requires typing ${VAULT_EXPORT_CONFIRMATION} to confirm.`);
  }
  const works = source.works.filter((work) => work.privacyState !== "vault");
  const workIds = new Set(works.map((work) => work.id));
  const memories = source.memories.filter(
    (memory) => memory.privacyState !== "vault" && workIds.has(memory.workId)
  );
  const relations = source.relations.filter(
    (relation) => workIds.has(relation.sourceWorkId) && workIds.has(relation.targetWorkId)
  );
  const lists = source.lists.filter((list) => list.privacyState !== "vault");
  const snapshot = {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    exportedAt: options.exportedAt,
    includesVault: includeVault,
    works,
    memories,
    lists,
    relations
  };
  if (includeVault) {
    return { ...snapshot, vaultNotes: [...source.vaultNotes ?? []] };
  }
  return snapshot;
}
function serializeBackup(snapshot) {
  return JSON.stringify(snapshot, null, 2);
}
var WORK_PRIVACY = /* @__PURE__ */ new Set(["public", "private", "vault"]);
var MEMORY_PRIVACY = /* @__PURE__ */ new Set(["public", "private", "vault"]);
function parseBackup(text) {
  const errors = [];
  if (text.length > MAX_IMPORT_CHARACTERS) {
    return { ok: false, errors: ["File is larger than the 25 MB import limit."] };
  }
  let raw;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, errors: ["File is not valid JSON."] };
  }
  if (!isRecord2(raw)) return { ok: false, errors: ["Backup root must be an object."] };
  if (raw["format"] !== BACKUP_FORMAT && raw["format"] !== "chronicles-vault-backup") errors.push("File is not a valid Grimoire or Chronicles backup.");
  if (raw["version"] !== BACKUP_VERSION) errors.push(`Unsupported backup version: ${String(raw["version"])}.`);
  if (typeof raw["exportedAt"] !== "string") errors.push("exportedAt is missing.");
  if (raw["includesVault"] === true) errors.push("Backups that include the vault are not accepted for import.");
  const works = asArray(raw["works"], "works", errors);
  const memories = asArray(raw["memories"], "memories", errors);
  const lists = asArray(raw["lists"], "lists", errors);
  const relations = asArray(raw["relations"], "relations", errors);
  const workIds = /* @__PURE__ */ new Set();
  for (const [index, work] of works.entries()) {
    if (!isRecord2(work)) {
      errors.push(`works[${index}] is not an object.`);
      continue;
    }
    const id = requireString(work["id"], `works[${index}].id`, errors);
    if (id) {
      if (workIds.has(id)) errors.push(`Duplicate work id: ${id}.`);
      workIds.add(id);
    }
    requireString(work["title"], `works[${index}].title`, errors, 500);
    if (typeof work["privacyState"] !== "string" || !WORK_PRIVACY.has(work["privacyState"])) {
      errors.push(`works[${index}].privacyState must be public or private.`);
    }
    if (Array.isArray(work["accessPaths"])) {
      for (const [pathIndex, path] of work["accessPaths"].entries()) {
        if (isRecord2(path) && typeof path["url"] === "string" && safeHttpUrl(path["url"]) === null) {
          errors.push(`works[${index}].accessPaths[${pathIndex}] has a non-http URL.`);
        }
      }
    }
  }
  for (const [index, memory] of memories.entries()) {
    if (!isRecord2(memory)) {
      errors.push(`memories[${index}] is not an object.`);
      continue;
    }
    requireString(memory["id"], `memories[${index}].id`, errors);
    if (typeof memory["workId"] !== "string" || !workIds.has(memory["workId"])) {
      errors.push(`memories[${index}] refers to a work that is not in this backup.`);
    }
    if (typeof memory["privacyState"] !== "string" || !MEMORY_PRIVACY.has(memory["privacyState"])) {
      errors.push(`memories[${index}].privacyState must be public or private.`);
    }
  }
  for (const [index, relation] of relations.entries()) {
    if (!isRecord2(relation)) continue;
    if (typeof relation["sourceWorkId"] !== "string" || !workIds.has(relation["sourceWorkId"])) {
      errors.push(`relations[${index}] source is not in this backup.`);
    }
    if (typeof relation["targetWorkId"] !== "string" || !workIds.has(relation["targetWorkId"])) {
      errors.push(`relations[${index}] target is not in this backup.`);
    }
  }
  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, value: raw };
}
var WORKS_CSV_HEADER = [
  "id",
  "title",
  "content_type",
  "genres",
  "tags",
  "privacy_state",
  "publication_state",
  "description",
  "access_url"
];
var FORMULA_PREFIX = /^[=+\-@\t\r]/;
function escapeCsvCell(value) {
  const guarded = FORMULA_PREFIX.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(guarded) || guarded.startsWith(" ") || guarded.endsWith(" ") ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}
function toCsv(rows) {
  return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n") + "\r\n";
}
function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\r" || char === "\n") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  if (inQuotes) throw new Error("CSV has an unterminated quoted field.");
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((candidate) => !(candidate.length === 1 && candidate[0] === ""));
}
function unguardCell(value) {
  return /^'[=+\-@\t\r]/.test(value) ? value.slice(1) : value;
}
function worksToCsv(works) {
  const rows = [[...WORKS_CSV_HEADER]];
  for (const work of works) {
    rows.push([
      work.id,
      work.title,
      work.contentType,
      work.genres.join("|"),
      work.tags.join("|"),
      work.privacyState,
      work.publicationState,
      work.description ?? "",
      work.accessPaths.find((path) => path.isPreferred)?.url ?? work.accessPaths.find((path) => path.url)?.url ?? ""
    ]);
  }
  return toCsv(rows);
}
function parseWorksCsv(text) {
  if (text.length > MAX_IMPORT_CHARACTERS) {
    return { candidates: [], invalid: [{ line: 0, reason: "File is larger than the 25 MB import limit." }] };
  }
  const rows = parseCsv(text);
  const [header, ...body] = rows;
  if (!header) return { candidates: [], invalid: [{ line: 0, reason: "File is empty." }] };
  const column = (name) => header.findIndex((cell) => cell.trim().toLowerCase() === name);
  const titleIndex = column("title");
  if (titleIndex < 0) return { candidates: [], invalid: [{ line: 1, reason: "Header needs a title column." }] };
  const candidates = [];
  const invalid = [];
  if (body.length > MAX_IMPORT_ROWS) {
    invalid.push({ line: 0, reason: `Only the first ${MAX_IMPORT_ROWS} rows are read.` });
  }
  body.slice(0, MAX_IMPORT_ROWS).forEach((cells, index) => {
    const line = index + 2;
    const get = (name) => {
      const at = column(name);
      return at >= 0 ? unguardCell((cells[at] ?? "").trim()) : "";
    };
    const title = get("title");
    if (!title) {
      invalid.push({ line, reason: "Title is empty." });
      return;
    }
    if (title.length > 500) {
      invalid.push({ line, reason: "Title is longer than 500 characters." });
      return;
    }
    const privacy = get("privacy_state") || "public";
    if (privacy === "vault") {
      invalid.push({ line, reason: "Vault rows cannot be imported from CSV." });
      return;
    }
    if (privacy !== "public" && privacy !== "private") {
      invalid.push({ line, reason: `Unknown privacy state: ${privacy}.` });
      return;
    }
    const url = get("access_url");
    if (url && safeHttpUrl(url) === null) {
      invalid.push({ line, reason: "Access URL must be http or https." });
      return;
    }
    candidates.push({
      title,
      contentType: get("content_type") || null,
      genres: splitList(get("genres")),
      tags: splitList(get("tags")),
      privacyState: privacy,
      description: get("description") || null,
      accessUrl: url || null,
      sourceLine: line
    });
  });
  return { candidates, invalid };
}
function splitList(value) {
  return value.split("|").map((part) => part.trim()).filter((part) => part.length > 0).slice(0, 50);
}
function memoriesToMarkdown(works, memories) {
  const titles = new Map(works.map((work) => [work.id, work.title]));
  const byWork = /* @__PURE__ */ new Map();
  for (const memory of memories) {
    if (memory.privacyState === "vault") continue;
    const bucket = byWork.get(memory.workId) ?? [];
    bucket.push(memory);
    byWork.set(memory.workId, bucket);
  }
  const sections = ["# Grimoire notes", ""];
  for (const [workId, items] of byWork) {
    sections.push(`## ${titles.get(workId) ?? workId}`, "");
    for (const memory of items) {
      sections.push(`### ${memory.title?.trim() || "Untitled"}`);
      sections.push(`_${memory.type} \xB7 spoiler: ${memory.spoilerLevel} \xB7 ${memory.createdAt}_`, "");
      if (memory.body) sections.push(memory.body.trim(), "");
    }
  }
  if (byWork.size === 0) sections.push("No notes yet.", "");
  return sections.join("\n");
}
var ANCHOR_PATTERN = /<A\s+[^>]*?HREF="([^"]+)"[^>]*>([\s\S]*?)<\/A>/gi;
var FOLDER_PATTERN = /<H3[^>]*>([\s\S]*?)<\/H3>/gi;
function decodeEntities(value) {
  return value.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").replace(/<[^>]+>/g, "").trim();
}
function parseBookmarksHtml(html) {
  const text = html.slice(0, MAX_IMPORT_CHARACTERS);
  const folderAt = [];
  for (const match of text.matchAll(FOLDER_PATTERN)) {
    folderAt.push({ index: match.index ?? 0, name: decodeEntities(match[1] ?? "") });
  }
  const seen = /* @__PURE__ */ new Set();
  const results = [];
  for (const match of text.matchAll(ANCHOR_PATTERN)) {
    if (results.length >= MAX_IMPORT_ROWS) break;
    const index = match.index ?? 0;
    const url = safeHttpUrl(decodeEntities(match[1] ?? ""));
    if (!url || seen.has(url)) continue;
    seen.add(url);
    const folder = folderAt.filter((entry) => entry.index < index).pop()?.name ?? null;
    results.push({ title: decodeEntities(match[2] ?? "") || url, url, folder });
  }
  return results;
}
function safeHttpUrl(value) {
  try {
    const parsed = new URL(value.trim());
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    parsed.hash = "";
    parsed.username = "";
    parsed.password = "";
    return parsed.toString();
  } catch {
    return null;
  }
}
function normalizeTitle(title) {
  return title.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}
function planWorkImport(candidates, existing) {
  const urls = new Set(existing.urls);
  const titles = new Set(existing.titles);
  const create15 = [];
  const duplicates = [];
  for (const candidate of candidates) {
    const url = candidate.accessUrl ? safeHttpUrl(candidate.accessUrl) : null;
    const key = normalizeTitle(candidate.title);
    if (url && urls.has(url)) {
      duplicates.push({ item: candidate, reason: "Access URL is already in the library." });
      continue;
    }
    if (titles.has(key)) {
      duplicates.push({ item: candidate, reason: "A work with this title already exists." });
      continue;
    }
    create15.push(candidate);
    titles.add(key);
    if (url) urls.add(url);
  }
  return { create: create15, duplicates };
}
function indexExisting(works) {
  const urls = /* @__PURE__ */ new Set();
  const titles = /* @__PURE__ */ new Set();
  for (const work of works) {
    titles.add(normalizeTitle(work.title));
    for (const url of work.urls) {
      const safe = safeHttpUrl(url);
      if (safe) urls.add(safe);
    }
  }
  return { urls, titles };
}
function isRecord2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function asArray(value, name, errors) {
  if (value === void 0) return [];
  if (!Array.isArray(value)) {
    errors.push(`${name} must be a list.`);
    return [];
  }
  return value;
}
function requireString(value, name, errors, maxLength = 200) {
  if (typeof value !== "string" || value.trim().length === 0) {
    errors.push(`${name} is required.`);
    return null;
  }
  if (value.length > maxLength) {
    errors.push(`${name} is longer than ${maxLength} characters.`);
    return null;
  }
  return value;
}

// src/transfer/apply-import.ts
import {
  AccessCapability as AccessCapability2,
  ContentOrigin as ContentOrigin4,
  ContentType as ContentType5,
  MemoryType as MemoryType4,
  PersonalState as PersonalState8,
  PrivacyState as PrivacyState7,
  PublicationState as PublicationState4,
  SourceHealth as SourceHealth4,
  SpoilerLevel as SpoilerLevel4,
  WorkRelationType as WorkRelationType4,
  createWebAccessPath
} from "@grimoire/domain";
function isEnumValue(enumObj, value) {
  return Object.values(enumObj).includes(value);
}
function oneOf(enumValues, value, fallback) {
  return value && isEnumValue(enumValues, value) ? value : fallback;
}
function webAccessPathFor(workId, url, label, isPreferred) {
  return createWebAccessPath({
    workId,
    url,
    label,
    provider: "import",
    isPreferred,
    health: SourceHealth4.Unknown,
    capabilities: [AccessCapability2.Read],
    provenance: null,
    lastOpenedAt: null
  });
}
function workParamsFor(input) {
  return {
    title: input.title,
    alternativeTitles: [],
    aliases: [...input.aliases ?? []],
    description: input.description,
    contentType: oneOf(ContentType5, input.contentType, ContentType5.WebNovel),
    origin: oneOf(ContentOrigin4, input.origin ?? null, ContentOrigin4.Unknown),
    publicationState: oneOf(
      PublicationState4,
      input.publicationState ?? null,
      PublicationState4.Unknown
    ),
    privacyState: input.privacyState === "vault" ? PrivacyState7.Vault : input.privacyState === "private" ? PrivacyState7.Private : PrivacyState7.Public,
    coverRef: input.coverRef ?? null,
    externalIds: [],
    genres: [...input.genres],
    tags: [...input.tags],
    mergedIntoWorkId: null
  };
}
function applyCandidateImport(candidates, options) {
  const repo = options?.repository ?? clientRepository;
  let created = 0;
  for (const candidate of candidates) {
    const url = candidate.accessUrl ? safeHttpUrl(candidate.accessUrl) : null;
    const item = repo.createWork(
      workParamsFor({
        title: candidate.title,
        description: candidate.description,
        contentType: candidate.contentType,
        genres: candidate.genres,
        tags: candidate.tags,
        privacyState: candidate.privacyState
      }),
      PersonalState8.Reading
    );
    if (url) {
      repo.addAccessPath(
        item.work.id,
        webAccessPathFor(item.work.id, url, new URL(url).host, true)
      );
    }
    created++;
  }
  if (repo === clientRepository && created > 0) {
    useLibraryStore.getState().refresh();
  }
  return created;
}
function applyBackupImport(snapshot, plannedWorkIds, options) {
  const repo = options?.repository ?? clientRepository;
  const shouldRestoreLists = options?.restoreLists !== false;
  const idMap = /* @__PURE__ */ new Map();
  const allWorkMap = /* @__PURE__ */ new Map();
  const existingByNormalizedTitle = /* @__PURE__ */ new Map();
  const existingById = /* @__PURE__ */ new Map();
  for (const existing of repo.getItems()) {
    existingById.set(existing.work.id, existing.work.id);
    existingByNormalizedTitle.set(
      normalizeTitle(existing.work.title),
      existing.work.id
    );
  }
  let accessPathsCreated = 0;
  let memoriesCreated = 0;
  let relationsCreated = 0;
  let worksCreated = 0;
  let listsRestored = 0;
  for (const work of snapshot.works) {
    if (!plannedWorkIds.has(work.id)) {
      const matchedId = existingById.get(work.id) ?? existingByNormalizedTitle.get(normalizeTitle(work.title));
      if (matchedId) {
        allWorkMap.set(work.id, matchedId);
      }
      continue;
    }
    const restored = restoreWork(repo, work);
    idMap.set(work.id, restored.id);
    allWorkMap.set(work.id, restored.id);
    worksCreated++;
    for (const path of work.accessPaths) {
      const safe = path.url ? safeHttpUrl(path.url) : null;
      if (!safe) continue;
      repo.addAccessPath(
        restored.id,
        webAccessPathFor(
          restored.id,
          safe,
          path.label ?? new URL(safe).host,
          path.isPreferred
        )
      );
      accessPathsCreated++;
    }
  }
  for (const memory of snapshot.memories) {
    const workId = idMap.get(memory.workId);
    if (!workId) continue;
    try {
      const isVault = memory.privacyState === "vault";
      const privacy = isVault ? PrivacyState7.Private : memory.privacyState === "private" ? PrivacyState7.Private : PrivacyState7.Public;
      repo.addMemory(workId, {
        type: oneOf(MemoryType4, memory.type, MemoryType4.Note),
        title: memory.title,
        body: memory.body,
        privacyState: privacy,
        spoilerLevel: oneOf(
          SpoilerLevel4,
          memory.spoilerLevel,
          SpoilerLevel4.SpoilerFree
        ),
        showInGlobalGallery: memory.showInGlobalGallery ?? true,
        accessPathId: null,
        progressAnchor: null,
        tags: [...memory.tags]
      });
      memoriesCreated++;
    } catch {
    }
  }
  for (const relation of snapshot.relations) {
    const source = idMap.get(relation.sourceWorkId);
    const target = idMap.get(relation.targetWorkId);
    if (!source || !target) continue;
    repo.linkWorkRelation(
      source,
      target,
      oneOf(
        WorkRelationType4,
        relation.relationType,
        WorkRelationType4.Sequel
      )
    );
    relationsCreated++;
  }
  if (shouldRestoreLists && snapshot.lists.length > 0) {
    const listsStore = useListsStore.getState();
    for (const backupList of snapshot.lists) {
      if (backupList.privacyState === "vault") continue;
      const targetTitleKey = normalizeTitle(backupList.title);
      let targetList = useListsStore.getState().myLists.find((l) => normalizeTitle(l.title) === targetTitleKey);
      if (!targetList) {
        targetList = listsStore.createList({
          title: backupList.title,
          description: backupList.description ?? void 0,
          privacyState: backupList.privacyState === "private" ? PrivacyState7.Private : PrivacyState7.Public
        });
      }
      const sortedItems = [...backupList.items].sort(
        (a, b) => a.position - b.position
      );
      for (const item of sortedItems) {
        const mappedWorkId = allWorkMap.get(item.workId) ?? existingById.get(item.workId);
        if (!mappedWorkId) continue;
        if (!useListsStore.getState().isWorkInList(targetList.id, mappedWorkId)) {
          listsStore.addWorkToList(
            targetList.id,
            mappedWorkId,
            item.personalNote ?? void 0
          );
        }
      }
      listsRestored++;
    }
  }
  const rawCategories = snapshot.categories;
  if (Array.isArray(rawCategories) && rawCategories.length > 0 && typeof window !== "undefined") {
    try {
      const rawStored = window.localStorage.getItem("cv:custom_categories:v1");
      const existingCategories = rawStored ? JSON.parse(rawStored) : [];
      const existingIds = new Set(existingCategories.map((c) => c.id));
      const newCats = rawCategories.filter((c) => c && c.id && !existingIds.has(c.id)).map((c) => ({
        ...c,
        workIds: Array.isArray(c.workIds) ? c.workIds.map((oldId) => allWorkMap.get(oldId) ?? oldId) : []
      }));
      if (newCats.length > 0) {
        window.localStorage.setItem("cv:custom_categories:v1", JSON.stringify([...existingCategories, ...newCats]));
      }
    } catch {
    }
  }
  const rawRelationships = snapshot.relationships;
  if (Array.isArray(rawRelationships) && rawRelationships.length > 0) {
    for (const rel of rawRelationships) {
      const mappedWorkId = idMap.get(rel.workId) ?? rel.workId;
      if (!mappedWorkId) continue;
      try {
        repo.updateRelationship(mappedWorkId, rel);
      } catch {
      }
    }
  }
  const rawRadar = snapshot.radarCaptures;
  if (Array.isArray(rawRadar) && rawRadar.length > 0 && typeof window !== "undefined") {
    try {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      useRadarStore.getState().importBatch({
        format: "grimoire-radar",
        version: 1,
        batchId: `import-${Date.now()}`,
        batchLabel: "Archive Import",
        browserSource: "unknown",
        capturedAt: now,
        items: rawRadar.map((r) => ({
          id: r.id || `radar-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          url: r.url,
          normalizedUrl: r.url.toLowerCase(),
          title: r.title,
          browserSource: r.browserSource || "chromium",
          capturedAt: r.capturedAt || now,
          batchId: `import-${Date.now()}`,
          batchLabel: "Archive Import",
          state: r.state || "pending",
          snippet: r.snippet || ""
        }))
      });
    } catch {
    }
  }
  const rawNotebook = snapshot.notebook_entries ?? snapshot.notebookEntries;
  if (Array.isArray(rawNotebook) && rawNotebook.length > 0) {
    try {
      const notebookStore = useNotebookStore.getState();
      const activatedSet = /* @__PURE__ */ new Set();
      for (const entry of rawNotebook) {
        if (!entry || !entry.workId) continue;
        const mappedWorkId = allWorkMap.get(entry.workId) ?? existingById.get(entry.workId) ?? entry.workId;
        if (!mappedWorkId) continue;
        if (!activatedSet.has(mappedWorkId)) {
          notebookStore.activateNotebook(mappedWorkId);
          activatedSet.add(mappedWorkId);
        }
        const validCategories = [
          "theory",
          "clue",
          "character",
          "question",
          "scratch"
        ];
        const category = validCategories.includes(entry.category) ? entry.category : "scratch";
        notebookStore.addEntry({
          workId: mappedWorkId,
          category,
          title: entry.title || "Untitled Note",
          body: entry.body || "",
          chapterHint: entry.chapterHint ?? null,
          pinned: Boolean(entry.pinned)
        });
      }
    } catch {
    }
  }
  const rawFandom = snapshot.fandom_links ?? snapshot.fandomLinks;
  if (Array.isArray(rawFandom) && rawFandom.length > 0) {
    try {
      const fandomStore = useFandomLinksStore.getState();
      for (const link of rawFandom) {
        if (!link || !link.workId || !link.url) continue;
        const mappedWorkId = allWorkMap.get(link.workId) ?? existingById.get(link.workId) ?? link.workId;
        if (!mappedWorkId) continue;
        try {
          fandomStore.addFandomLink({
            workId: mappedWorkId,
            communityName: link.communityName || link.name || "Community Wiki",
            provider: link.provider,
            canonicalTitle: link.canonicalTitle || link.communityName,
            url: link.url,
            language: link.language || "en",
            isPreferred: Boolean(link.isPreferred),
            provenance: link.provenance || "import"
          });
        } catch {
        }
      }
    } catch {
    }
  }
  if (repo === clientRepository && worksCreated > 0) {
    useLibraryStore.getState().refresh();
  }
  return {
    worksCreated,
    accessPathsCreated,
    memoriesCreated,
    relationsCreated,
    listsRestored
  };
}
function restoreWork(repo, work) {
  return repo.createWork(
    workParamsFor({
      title: work.title,
      description: work.description,
      contentType: work.contentType,
      genres: work.genres,
      tags: work.tags,
      privacyState: work.privacyState === "vault" ? "vault" : work.privacyState === "private" ? "private" : "public",
      aliases: work.aliases,
      origin: work.origin,
      publicationState: work.publicationState,
      coverRef: work.coverRef && safeHttpUrl(work.coverRef) ? work.coverRef : null
    }),
    PersonalState8.Reading
  ).work;
}

// src/stores/use-trash-store.ts
import { create as create14 } from "zustand";
var useTrashStore = create14((set, get) => {
  function computeState() {
    const deletedWorks = clientRepository.getDeletedWorks();
    const deletedMemories = clientRepository.getDeletedMemories();
    const deletedLists = useListsStore.getState().getDeletedLists();
    const totalTrashCount = deletedWorks.length + deletedMemories.length + deletedLists.length;
    return { deletedWorks, deletedMemories, deletedLists, totalTrashCount };
  }
  const initial = computeState();
  return {
    deletedWorks: initial.deletedWorks,
    deletedMemories: initial.deletedMemories,
    deletedLists: initial.deletedLists,
    totalTrashCount: initial.totalTrashCount,
    activeFilter: "all",
    isLoading: false,
    setActiveFilter: (activeFilter) => set({ activeFilter }),
    refreshTrash: () => {
      set(computeState());
    },
    restoreWork: (workId) => {
      clientRepository.restoreWork(workId);
      try {
        useLibraryStore.getState().refresh();
      } catch {
      }
      set(computeState());
    },
    purgeWork: (workId) => {
      clientRepository.purgeWork(workId);
      set(computeState());
    },
    restoreMemory: (memoryId) => {
      clientRepository.restoreMemory(memoryId);
      set(computeState());
    },
    purgeMemory: (memoryId) => {
      clientRepository.purgeMemory(memoryId);
      set(computeState());
    },
    restoreList: (listId) => {
      useListsStore.getState().restoreList(listId);
      set(computeState());
    },
    purgeList: (listId) => {
      useListsStore.getState().purgeList(listId);
      set(computeState());
    },
    emptyAllTrash: () => {
      clientRepository.purgeAllTrash();
      const deletedLists = useListsStore.getState().getDeletedLists();
      for (const list of deletedLists) {
        useListsStore.getState().purgeList(list.id);
      }
      try {
        useLibraryStore.getState().refresh();
      } catch {
      }
      set(computeState());
    },
    autoPurgeExpired: (retentionDays = 30) => {
      clientRepository.purgeExpiredTrash(retentionDays);
      const cutoffMs = Date.now() - retentionDays * 24 * 60 * 60 * 1e3;
      const deletedLists = useListsStore.getState().getDeletedLists();
      for (const list of deletedLists) {
        if (list.deletedAt && new Date(list.deletedAt).getTime() < cutoffMs) {
          useListsStore.getState().purgeList(list.id);
        }
      }
      set(computeState());
    }
  };
});
export {
  AVAILABLE_USER_PROFILES,
  BACKUP_FORMAT,
  BACKUP_VERSION,
  BrowserRepositoryAdapter,
  COMMUNITY_LISTS_SEED,
  ClientRepository,
  DEFAULT_USER_ACCOUNT,
  DEFAULT_WORK_COVERS,
  MAX_IMPORT_CHARACTERS,
  MAX_IMPORT_ROWS,
  SEARCH_CATEGORY_LABELS,
  SEARCH_CATEGORY_ORDER,
  VAULT_EXPORT_CONFIRMATION,
  WORKS_CSV_HEADER,
  applyBackupImport,
  applyCandidateImport,
  browserRepository,
  buildBackup,
  buildUpdateFeedId,
  clientRepository,
  collectLibraryUpdateIds,
  contentTypeLabel,
  crc32,
  createInitialArchiveSeed,
  createStoredZip,
  escapeCsvCell,
  escapeHtml,
  escapeMarkdown,
  evaluatePublication,
  generateLocalRecommendations,
  getActiveClientRepository,
  getActiveRepository,
  indexExisting,
  isPrivateTagName,
  makeSnippet,
  memoriesToMarkdown,
  normalizeForScan,
  normalizeTitle,
  parseBackup,
  parseBookmarksHtml,
  parseCsv,
  parseRadarImport,
  parseWorksCsv,
  planWorkImport,
  privateNeedles,
  readStoredZip,
  renderPublicationHtml,
  renderPublicationMarkdown,
  runGlobalSearch,
  safeHost,
  safeHttpUrl,
  sanitizeRadarCaptureUrl,
  selectFilteredRadarItems,
  selectFilteredWorks,
  selectGroupedByWork,
  serializeBackup,
  setActiveClientRepository,
  setActiveRepository,
  toCsv,
  tokenizeQuery,
  useCategoriesStore,
  useFanCreationsStore,
  useFandomLinksStore,
  useFriendsStore,
  useLibraryStore,
  useListsStore,
  useMergeWizardStore,
  useNotebookStore,
  useRadarStore,
  useReadingQueueStore,
  useRecommendationsStore,
  useReturnDeskStore,
  useTrashStore,
  useUpdatesStore,
  useWorkDetailStore,
  useWorkRelationsStore,
  validateZipEntryName,
  worksToCsv
};
